<template>
  <div class="public-bookings-page">
    <SidebarComponent />
    <div class="dashboard-layout">
      <div class="content-container">
        <div class="card-container">
          <div class="titolo-card">
            <h1>Prenotazioni pubbliche</h1>
            <h3>Consulenze online e in presenza con Fusco Fit.</h3>
          </div>

          <div v-if="bookingView === 'current'" class="booking-view-action">
            <button type="button" class="btn ghost staff-button-neutral" @click="openTimeline">
              <span class="material-icons" aria-hidden="true">format_list_bulleted</span>
              Elenco cronologico
            </button>
            <span>Tutte le prenotazioni, presenze e acquisti successivi.</span>
          </div>

          <template v-if="bookingView === 'current'">
          <section class="toolbar-card card-surface">
            <div class="filters-header filters-header--with-action">
              <button
                type="button"
                class="accordion-toggle staff-button-neutral"
                :aria-expanded="filtersOpen"
                @click="filtersOpen = !filtersOpen"
              >
                <div class="toggle-copy">
                  <span class="toggle-title">Filtri e azioni</span>
                  <span class="toggle-subtitle">Data prenotazione, refresh, date disponibili</span>
                </div>
                <span class="material-icons" aria-hidden="true">
                  {{ filtersOpen ? 'expand_less' : 'expand_more' }}
                </span>
              </button>
              <div class="booking-create-actions">
              <button
                class="btn primary booking-add-action staff-button-positive"
                type="button"
                title="Aggiungi prenotazione"
                aria-label="Aggiungi prenotazione"
                :disabled="bookingSaving"
                @click="openManualBooking"
              >
                <span class="material-icons" aria-hidden="true">add_circle</span>
                Aggiungi
              </button>
              <button
                v-if="canManagePublicCalendar"
                type="button"
                class="btn ghost booking-block-action staff-button-neutral"
                :disabled="blockSaving || bookingSaving || isLoading || !dashboardLoaded"
                @click="openBookingBlock"
              >
                <span class="material-icons" aria-hidden="true">block</span>
                Aggiungi blocco
              </button>
              </div>
            </div>

            <transition name="accordion">
              <div v-show="filtersOpen" class="accordion-panel">
                <div class="toolbar">
                  <div class="toolbar-group toolbar-group--sm">
                    <label class="toolbar-label" for="public-booking-date">Data</label>
                    <input
                      id="public-booking-date"
                      v-model="selectedDate"
                      type="date"
                      class="input date-range-input date-range-input--short"
                    />
                  </div>

                  <div class="toolbar-actions">
                    <button
                      class="btn ghost staff-button-neutral"
                      type="button"
                      :disabled="!selectedDate"
                      @click="selectedDate = ''"
                    >
                      Mostra da oggi
                    </button>
                    <button class="btn ghost staff-button-neutral" type="button" :disabled="isLoading" @click="fetchBookings">
                      {{ isLoading ? 'Aggiorno...' : 'Aggiorna lista' }}
                    </button>
                  </div>
                </div>

                <div class="meeting-mode-filters" role="group" aria-label="Modalità appuntamento">
                  <span class="meeting-mode-filters__label">Modalità appuntamento</span>
                  <button
                    type="button"
                    :class="{ active: selectedMeetingMode === 'online' }"
                    :aria-pressed="selectedMeetingMode === 'online'"
                    :title="selectedMeetingMode === 'online' ? 'Mostra tutte le modalità' : 'Mostra solo prenotazioni online'"
                    @click="toggleMeetingModeFilter('online')"
                  >
                    <span class="material-icons" aria-hidden="true">videocam</span>
                    Online
                  </button>
                  <button
                    type="button"
                    :class="{ active: selectedMeetingMode === 'in_person' }"
                    :aria-pressed="selectedMeetingMode === 'in_person'"
                    :title="selectedMeetingMode === 'in_person' ? 'Mostra tutte le modalità' : 'Mostra solo prenotazioni in presenza'"
                    @click="toggleMeetingModeFilter('in_person')"
                  >
                    <span class="material-icons" aria-hidden="true">location_on</span>
                    In presenza
                  </button>
                </div>

                <div
                  v-if="currentAndFutureAvailableDates.length"
                  class="date-chips"
                  role="group"
                  aria-label="Date di oggi e future"
                >
                  <button
                    v-for="dateKey in currentAndFutureAvailableDates"
                    :key="dateKey"
                    type="button"
                    :class="{ active: selectedDate === dateKey }"
                    :aria-pressed="selectedDate === dateKey"
                    @click="selectedDate = dateKey"
                  >
                    {{ formatDateLabel(dateKey) }}
                  </button>
                </div>

                <section v-if="dashboardLoaded" class="past-dates-disclosure">
                  <h3 class="past-dates-heading">
                    <button
                      id="past-dates-toggle"
                      type="button"
                      class="accordion-toggle accordion-toggle--nested staff-button-neutral"
                      :aria-expanded="pastDatesOpen"
                      aria-controls="past-dates-panel"
                      @click="pastDatesOpen = !pastDatesOpen"
                    >
                      <span class="toggle-copy">
                        <span class="toggle-title">Date passate</span>
                        <span class="toggle-subtitle">
                          {{ historyLoaded ? `${pastAvailableDates.length} date, dalla piu recente` : 'Carica le prenotazioni passate' }}
                        </span>
                      </span>
                      <span class="material-icons" aria-hidden="true">
                        {{ pastDatesOpen ? 'expand_less' : 'expand_more' }}
                      </span>
                    </button>
                  </h3>

                  <transition name="accordion">
                    <div
                      id="past-dates-panel"
                      v-show="pastDatesOpen"
                      class="accordion-panel accordion-panel--nested"
                      role="region"
                      aria-labelledby="past-dates-toggle"
                    >
                      <div v-if="!historyLoaded && isLoading" class="bookings-loading" role="status">
                        Caricamento prenotazioni passate...
                      </div>
                      <div v-else-if="!historyLoaded && errorMessage" class="bookings-load-error" role="alert">
                        <span>Non riesco a caricare lo storico.</span>
                        <button class="btn ghost staff-button-neutral" type="button" @click="ensureBookingHistory">Riprova storico</button>
                      </div>
                      <div v-else-if="historyLoaded && !pastAvailableDates.length" class="empty-state muted">
                        Nessuna prenotazione passata.
                      </div>
                      <div v-if="historyLoaded" class="date-chips" role="group" aria-label="Date passate">
                        <button
                          v-for="dateKey in pastAvailableDates"
                          :key="dateKey"
                          type="button"
                          :class="{ active: selectedDate === dateKey }"
                          :aria-pressed="selectedDate === dateKey"
                          @click="selectedDate = dateKey"
                        >
                          {{ formatDateLabel(dateKey) }}
                        </button>
                      </div>
                    </div>
                  </transition>
                </section>

              </div>
            </transition>
          </section>

          <section v-if="canViewPublicCalendar" :class="['schedule-grid', { 'schedule-grid--calendar-locked': !canManagePublicCalendar }]">
            <section v-if="canManagePublicCalendar" class="schedule-card schedule-editor-card card-surface">
              <div class="filters-header">
                <button
                  type="button"
                  class="accordion-toggle staff-button-neutral"
                  :aria-expanded="scheduleEditorOpen"
                  @click="scheduleEditorOpen = !scheduleEditorOpen"
                >
                  <div class="toggle-copy">
                    <span class="toggle-title">Calendario pubblico</span>
                    <span class="toggle-subtitle">Crea o modifica una data prenotabile</span>
                  </div>
                  <span class="material-icons" aria-hidden="true">
                    {{ scheduleEditorOpen ? 'expand_less' : 'expand_more' }}
                  </span>
                </button>
              </div>

              <transition name="accordion">
                <div v-show="scheduleEditorOpen" class="accordion-panel accordion-panel--plain">
                  <div class="section-heading schedule-editor-heading">
                    <div>
                      <span class="section-kicker">
                        <span class="material-icons" aria-hidden="true">event_available</span>
                        Calendario pubblico
                      </span>
                      <h2>{{ editingDateKey ? 'Modifica data prenotabile' : 'Aggiungi data prenotabile' }}</h2>
                      <p>Apri una data precisa senza modificare Firestore manualmente.</p>
                    </div>
                    <button
                      v-if="editingDateKey"
                      class="btn ghost compact-btn staff-button-cancel"
                      type="button"
                      :disabled="scheduleSaving"
                      @click="resetDateForm"
                    >
                      <span class="material-icons" aria-hidden="true">close</span>
                      Annulla
                    </button>
                  </div>

                  <form class="schedule-form schedule-form--editor" @submit.prevent="saveBookingDate">
                    <div class="form-block form-block--date">
                      <div class="form-block-title">
                        <span class="material-icons" aria-hidden="true">calendar_today</span>
                        <span>Data</span>
                      </div>
                      <div class="toolbar-group">
                        <label class="toolbar-label" for="booking-config-date">Giorno</label>
                        <input
                          id="booking-config-date"
                          v-model="dateForm.dateKey"
                          type="date"
                          class="input input--large"
                          :disabled="!!editingDateKey || scheduleSaving"
                          required
                        />
                      </div>
                    </div>

                    <div class="form-block form-block--time">
                      <div class="form-block-title">
                        <span class="material-icons" aria-hidden="true">schedule</span>
                        <span>Orario</span>
                      </div>
                      <div class="time-fields">
                        <div class="toolbar-group">
                          <label class="toolbar-label" for="booking-config-start">Dalle</label>
                          <input
                            id="booking-config-start"
                            v-model="dateForm.start"
                            type="time"
                            class="input"
                            :disabled="scheduleSaving"
                            required
                          />
                        </div>

                        <div class="toolbar-group">
                          <label class="toolbar-label" for="booking-config-end">Alle</label>
                          <input
                            id="booking-config-end"
                            v-model="dateForm.end"
                            type="time"
                            class="input"
                            :disabled="scheduleSaving"
                            required
                          />
                        </div>

                        <div class="toolbar-group">
                          <label class="toolbar-label" for="booking-config-slot">Slot</label>
                          <select
                            id="booking-config-slot"
                            v-model.number="dateForm.slotMinutes"
                            class="input"
                            :disabled="scheduleSaving"
                          >
                            <option :value="10">10 min</option>
                            <option :value="15">15 min</option>
                            <option :value="20">20 min</option>
                            <option :value="30">30 min</option>
                            <option :value="45">45 min</option>
                            <option :value="60">60 min</option>
                          </select>
                          <small class="booking-edit-hint">La durata si applica anche agli appuntamenti già prenotati della giornata, mantenendo gli orari di inizio.</small>
                        </div>
                      </div>
                    </div>

                    <div class="form-block form-block--visibility">
                      <div class="form-block-title">
                        <span class="material-icons" aria-hidden="true">visibility</span>
                        <span>Visibilita</span>
                      </div>
                      <div class="visibility-row">
                        <div class="toolbar-group">
                          <label class="toolbar-label" for="booking-config-visibility">Stato</label>
                          <select
                            id="booking-config-visibility"
                            v-model="dateForm.visibility"
                            class="input"
                            :disabled="scheduleSaving"
                          >
                            <option value="public">Visibile</option>
                            <option value="hidden">Nascosta</option>
                          </select>
                        </div>
                        <div
                          :class="[
                            'visibility-preview',
                            dateForm.visibility === 'public' ? 'is-public' : 'is-hidden'
                          ]"
                        >
                          <span class="material-icons" aria-hidden="true">
                            {{ dateForm.visibility === 'public' ? 'public' : 'visibility_off' }}
                          </span>
                          <span>{{ dateForm.visibility === 'public' ? 'Online' : 'Nascosta' }}</span>
                        </div>
                      </div>
                    </div>

                    <div class="toolbar-actions schedule-actions schedule-actions--editor">
                      <button class="btn primary save-date-btn staff-button-positive" type="submit" :disabled="scheduleSaving">
                        <span class="material-icons" aria-hidden="true">
                          {{ editingDateKey ? 'save' : 'add_circle' }}
                        </span>
                        {{ scheduleSaving ? 'Salvo...' : (editingDateKey ? 'Salva modifiche' : 'Crea data') }}
                      </button>
                    </div>
                  </form>

                  <div
                    v-if="scheduleMessage || scheduleError || formVisibilityHint"
                    class="feedback-row schedule-feedback"
                  >
                    <span v-if="scheduleMessage" class="feedback-pill success">
                      <span class="material-icons" aria-hidden="true">check_circle</span>
                      {{ scheduleMessage }}
                    </span>
                    <span v-if="formVisibilityHint" class="feedback-pill info">
                      <span class="material-icons" aria-hidden="true">info</span>
                      {{ formVisibilityHint }}
                    </span>
                    <span v-if="scheduleError" class="feedback-pill danger">
                      <span class="material-icons" aria-hidden="true">error</span>
                      {{ scheduleError }}
                    </span>
                  </div>
                </div>
              </transition>
            </section>

            <section class="schedule-card card-surface">
              <div class="filters-header">
                <button
                  type="button"
                  class="accordion-toggle staff-button-neutral"
                  :aria-expanded="availableDatesOpen"
                  @click="availableDatesOpen = !availableDatesOpen"
                >
                  <div class="toggle-copy">
                    <span class="toggle-title">Date prenotabili</span>
                    <span class="toggle-subtitle">Gestisci visibilita e orari delle date configurate</span>
                  </div>
                  <span class="material-icons" aria-hidden="true">
                    {{ availableDatesOpen ? 'expand_less' : 'expand_more' }}
                  </span>
                </button>
              </div>

              <transition name="accordion">
                <div v-show="availableDatesOpen" class="accordion-panel accordion-panel--plain">
                  <div class="section-heading">
                    <div>
                      <h2>Date prenotabili</h2>
                      <p>Gestisci visibilita e orari delle date configurate.</p>
                    </div>
                    <button class="btn ghost staff-button-neutral" type="button" :disabled="serviceLoading || scheduleSaving" @click="fetchServiceConfig">
                      {{ serviceLoading ? 'Aggiorno...' : 'Aggiorna date' }}
                    </button>
                  </div>

                  <div class="month-filter-row">
                    <div class="toolbar-group toolbar-group--month">
                      <label class="toolbar-label" for="booking-config-month">Mese</label>
                      <select
                        id="booking-config-month"
                        v-model="selectedConfigMonth"
                        class="input"
                      >
                        <option
                          v-for="month in monthOptions"
                          :key="month.value"
                          :value="month.value"
                        >
                          {{ month.label }}
                        </option>
                      </select>
                    </div>
                    <div class="toolbar-group toolbar-group--year">
                      <label class="toolbar-label" for="booking-config-year">Anno</label>
                      <select
                        id="booking-config-year"
                        v-model="selectedConfigYear"
                        class="input"
                      >
                        <option
                          v-for="year in configYearOptions"
                          :key="year"
                          :value="year"
                        >
                          {{ year }}
                        </option>
                      </select>
                    </div>
                    <span class="month-filter-summary">
                      {{ monthFilteredConfiguredDates.length }} date nel mese selezionato
                    </span>
                  </div>

                  <div v-if="serviceLoading" class="bookings-loading" role="status" aria-live="polite">
                    <span class="material-icons" aria-hidden="true">sync</span>
                    {{ dashboardLoaded ? 'Aggiornamento date...' : 'Caricamento date...' }}
                  </div>

                  <div v-if="monthFilteredConfiguredDates.length" class="tickets schedule-table-wrap" :aria-busy="serviceLoading">
                    <table role="table" aria-label="Date prenotabili" class="public-bookings-mobile-table public-bookings-calendar-table users-table schedule-table">
                      <thead role="rowgroup">
                        <tr role="row">
                          <th scope="col" role="columnheader">Data</th>
                          <th scope="col" role="columnheader">Orari</th>
                          <th scope="col" role="columnheader">Slot</th>
                          <th scope="col" role="columnheader">Visibilita</th>
                          <th scope="col" role="columnheader">Prenotazioni</th>
                          <th scope="col" role="columnheader">Pubblico</th>
                          <th scope="col" role="columnheader" v-if="canManagePublicCalendar">Azioni</th>
                        </tr>
                      </thead>
                      <tbody role="rowgroup">
                        <tr role="row" v-for="date in monthFilteredConfiguredDates" :key="date.dateKey">
                          <td class="public-booking-name-cell" data-label="Data" role="cell">
                            <strong>{{ formatDateLabel(date.dateKey) }}</strong>
                            <small class="mono">{{ date.dateKey }}</small>
                          </td>
                          <td class="public-booking-time-cell mono" data-label="Orari" role="cell">{{ date.start }} - {{ date.end }}</td>
                          <td data-label="Durata slot" role="cell">{{ date.slotMinutes }} min</td>
                          <td data-label="Visibilità" role="cell">
                            <span :class="['status-badge', date.visibility === 'public' ? 'confirmed' : 'cancelled']">
                              {{ date.visibility === 'public' ? 'Visibile' : 'Nascosta' }}
                            </span>
                          </td>
                          <td data-label="Prenotazioni" role="cell">{{ date.bookingCount == null ? 'Da caricare' : date.bookingCount }}</td>
                          <td class="public-booking-wide-cell" data-label="Pubblico" role="cell">
                            <span :class="['date-public-state', date.isPublicNow ? 'ok' : 'muted-state']">
                              {{ date.publicLabel }}
                            </span>
                          </td>
                          <td v-if="canManagePublicCalendar" class="public-booking-actions-cell" data-label="Azioni" role="cell">
                            <div class="buttons-container">
                              <button class="staff-button-positive"
                                type="button"
                                title="Modifica data"
                                aria-label="Modifica data"
                                :disabled="scheduleSaving"
                                @click="editBookingDate(date)"
                              >
                                <span class="material-icons">edit</span>
                                <span class="public-bookings-mobile-only">Modifica</span>
                              </button>
                              <button class="staff-button-positive"
                                type="button"
                                :title="date.visibility === 'public' ? 'Nascondi data' : 'Rendi visibile'"
                                :aria-label="date.visibility === 'public' ? 'Nascondi data' : 'Rendi visibile'"
                                :disabled="scheduleSaving"
                                @click="toggleDateVisibility(date)"
                              >
                                <span class="material-icons">
                                  {{ date.visibility === 'public' ? 'visibility_off' : 'visibility' }}
                                </span>
                                <span class="public-bookings-mobile-only">{{ date.visibility === 'public' ? 'Nascondi' : 'Mostra' }}</span>
                              </button>
                              <button class="staff-button-danger"
                                type="button"
                                title="Elimina data"
                                aria-label="Elimina data"
                                :disabled="scheduleSaving || serviceLoading || !!errorMessage || date.bookingCount == null || date.bookingCount > 0"
                                @click="deleteBookingDate(date)"
                              >
                                <span class="material-icons">delete</span>
                                <span class="public-bookings-mobile-only">Elimina</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div v-else-if="dashboardLoaded && !serviceLoading && !errorMessage" class="empty-state muted">
                    Nessuna data configurata per il mese selezionato.
                  </div>

                  <div v-if="serviceError" class="feedback-row">
                    <span class="error">{{ serviceError }}</span>
                  </div>
                </div>
              </transition>
            </section>
          </section>

          </template>

          <div v-if="isLoading" class="bookings-loading" role="status" aria-live="polite">
            <span class="material-icons" aria-hidden="true">sync</span>
            {{ dashboardLoaded ? 'Aggiornamento prenotazioni...' : 'Caricamento prenotazioni...' }}
          </div>

          <div v-if="errorMessage" class="bookings-load-error" role="alert">
            <span>{{ errorMessage }}</span>
            <button class="btn ghost staff-button-neutral" type="button" :disabled="isLoading" @click="fetchBookings">Riprova caricamento</button>
          </div>

          <div v-if="dashboardLoaded && bookingView === 'current'" class="dataset-summary">
            <span>{{ historyLoaded ? 'Totale' : 'Da oggi' }} {{ bookings.length }} prenotazioni</span>
            <span v-if="selectedDate">
              Mostrando {{ filteredBookings.length }} prenotazioni del {{ formatDateLabel(selectedDate) }}
            </span>
            <span v-else>Mostrando {{ filteredBookings.length }} prenotazioni di oggi e future</span>
            <span v-if="selectedMeetingMode">Modalità: {{ selectedMeetingMode === 'in_person' ? 'In presenza' : 'Online' }}</span>
            <span>{{ currentAndFutureAvailableDates.length }} date da oggi</span>
          </div>

          <div v-if="bookingMessage || (bookingError && !editingBookingId)" class="feedback-row schedule-feedback">
            <span v-if="bookingMessage" class="feedback-pill success">
              <span class="material-icons" aria-hidden="true">check_circle</span>
              {{ bookingMessage }}
            </span>
            <span v-if="bookingError && !editingBookingId" class="feedback-pill danger">
              <span class="material-icons" aria-hidden="true">error</span>
              {{ bookingError }}
            </span>
          </div>

          <PublicBookingsTimeline
            v-if="bookingView === 'timeline'"
            :bookings="bookings"
            :commercial-statuses="commercialStatusByBookingId"
            :purchase-info="purchaseInfo"
            :situation-info="situationInfo"
            :save-note="saveBookingNote"
            :attendance-saving="attendanceSaving"
            :attendance-pending="attendancePending"
            :attendance-errors="attendanceErrors"
            :loading="isLoading"
            :history-loaded="historyLoaded"
            :can-manage="canManagePublicCalendar"
            :actions-disabled="isLoading || bookingSaving || reminderSaving || !!errorMessage"
            @visible-bookings="timelineVisibleBookings = $event"
            @attendance-change="saveAttendance($event.booking, $event.participated)"
            @open-profile="openUserProfile"
            @follow-up="openFollowUpReminder"
            @refresh="fetchBookings"
            @back="bookingView = 'current'"
          />

          <template v-else>
          <div v-if="currentBookingRows.length" class="tickets" :aria-busy="isLoading">
            <table role="table" aria-label="Prenotazioni pubbliche" class="public-bookings-mobile-table public-bookings-list-table users-table">
              <thead role="rowgroup">
                <tr role="row">
                  <th scope="col" role="columnheader">Data</th>
                  <th scope="col" role="columnheader">Orario</th>
                  <th scope="col" role="columnheader">Cliente</th>
                  <th scope="col" role="columnheader">Telefono</th>
                  <th scope="col" role="columnheader">Note</th>
                  <th scope="col" role="columnheader">Servizio</th>
                  <th scope="col" role="columnheader">Situazione</th>
                  <th scope="col" role="columnheader">Stato</th>
                  <th scope="col" role="columnheader">Partecipato</th>
                  <th scope="col" role="columnheader">Profilo</th>
                  <th scope="col" role="columnheader" v-if="canManagePublicCalendar">Azioni</th>
                </tr>
              </thead>
              <tbody role="rowgroup">
                <template v-for="booking in currentBookingRows" :key="`${booking.isBlock ? 'block' : 'booking'}-${booking.id}`">
                <tr v-if="booking.isBlock" role="row" class="public-booking-block-row">
                  <td data-label="Data" role="cell" class="public-booking-block-date">
                    <strong>{{ formatDateLabel(booking.dateKey) }}</strong>
                  </td>
                  <td data-label="Fascia bloccata" role="cell" class="public-booking-block-time mono">
                    <span class="public-booking-block-interval">{{ booking.start }} <span aria-hidden="true">–</span> {{ booking.end }}</span>
                  </td>
                  <td :colspan="8" role="cell" class="public-booking-block-detail">
                    <div class="public-booking-block-summary">
                      <span class="material-icons public-booking-block-icon" aria-hidden="true">block</span>
                      <div class="public-booking-block-copy">
                        <strong class="public-booking-block-title">Prenotazioni bloccate</strong>
                        <span v-if="booking.note" class="public-booking-block-note">{{ booking.note }}</span>
                        <small>Nessuna nuova prenotazione manuale o online in questa fascia.</small>
                      </div>
                    </div>
                  </td>
                  <td v-if="canManagePublicCalendar" role="cell" class="public-booking-block-actions">
                    <button type="button" class="btn ghost staff-button-neutral" :disabled="blockSaving || isLoading"
                      :aria-label="`Rimuovi blocco del ${formatDateLabel(booking.dateKey)} dalle ${booking.start} alle ${booking.end}`"
                      @click="removeBookingBlock(booking)">
                      <span class="material-icons" aria-hidden="true">lock_open</span>
                      Rimuovi blocco
                    </button>
                  </td>
                </tr>
                <tr role="row"
                  v-else
                  :class="{
                    'booking-row--missing-phone': bookingMissingPhone(booking),
                    'booking-row--in-person': bookingMeetingMode(booking) === 'in_person',
                  }"
                >
                  <td class="public-booking-date-cell" data-label="Data" role="cell">
                    <div class="booking-date-content">
                      <span
                        :class="['booking-meeting-icon', { 'is-in-person': bookingMeetingMode(booking) === 'in_person' }]"
                        role="img"
                        :title="bookingMeetingModeLabel(booking)"
                        :aria-label="bookingMeetingModeLabel(booking)"
                      >
                        <span class="material-icons" aria-hidden="true">
                          {{ bookingMeetingMode(booking) === 'in_person' ? 'location_on' : 'videocam' }}
                        </span>
                      </span>
                      <span>{{ formatDateLabel(booking.dateKey) }}</span>
                    </div>
                    <span class="public-bookings-mobile-only public-booking-meeting-copy">{{ bookingMeetingModeLabel(booking) }}</span>
                  </td>
                  <td class="public-booking-time-cell mono" data-label="Orario" role="cell">{{ booking.timeKey || '-' }}</td>
                  <td class="public-booking-name-cell" data-label="Cliente" role="cell">
                    <strong>{{ booking.fullName || '-' }}</strong>
                    <small class="mono">{{ booking.id }}</small>
                  </td>
                  <td class="public-booking-phone-cell public-booking-wide-cell" data-label="Telefono" role="cell">
                    <span class="public-bookings-desktop-only">{{ booking.phone || '-' }}</span>
                    <a v-if="booking.phone" class="public-bookings-mobile-only public-booking-phone-link" :href="'tel:' + String(booking.phone).replace(/[^\d+]/g, '')">{{ booking.phone }}</a>
                    <span v-else class="public-bookings-mobile-only">Non indicato</span>
                  </td>
                  <td class="public-booking-notes-cell public-booking-wide-cell booking-note-cell" data-label="Note" role="cell">
                    <PublicBookingNote
                      :note="booking.note || ''"
                      :client-name="booking.fullName || booking.email || 'cliente'"
                      :editable="canManagePublicCalendar"
                      :disabled="isLoading || bookingSaving || reminderSaving || !!errorMessage"
                      :save-note="note => saveBookingNote(booking, note)"
                    />
                  </td>
                  <td class="public-booking-service-cell" data-label="Servizio" role="cell">
                    <span
                      :class="[
                        'booking-origin-badge',
                        bookingOriginClass(booking),
                        { 'is-outside-calendar': bookingIsOutsideCalendar(booking) },
                      ]"
                    >
                      {{ bookingOriginLabel(booking) }}
                    </span>
                  </td>
                  <td class="public-booking-commercial-cell public-booking-wide-cell commercial-status-cell" data-label="Situazione" role="cell">
                    <span
                      :class="['commercial-status-icon', commercialStatusClass(booking)]"
                      :title="commercialStatusTitle(booking)"
                      :aria-label="commercialStatusTitle(booking)"
                    >
                      <span class="material-icons" aria-hidden="true">
                        {{ commercialStatusIcon(booking) }}
                      </span>
                    </span>
                    <span class="public-bookings-mobile-only public-booking-commercial-copy">{{ commercialStatusTitle(booking) }}</span>
                  </td>
                  <td class="public-booking-status-cell" data-label="Stato" role="cell">
                    <span :class="['status-badge', booking.status || 'unknown']">
                      {{ booking.status || 'N/D' }}
                    </span>
                  </td>
                  <td class="public-booking-attendance-cell public-booking-wide-cell" data-label="Partecipato" role="cell">
                    <PublicBookingAttendance :booking="booking" :saving="!!attendanceSaving[booking.id]" :pending-value="attendancePending[booking.id]" :error="attendanceErrors[booking.id]" :disabled="isLoading || bookingSaving || !canManagePublicCalendar" @change="saveAttendance(booking, $event)" />
                  </td>
                  <td class="public-booking-profile-cell public-booking-wide-cell" data-label="Profilo" role="cell">
                    <div class="buttons-container">
                      <button class="staff-button-neutral"
                        v-if="booking.userId"
                        type="button"
                        @click="openUserProfile(booking.userId)"
                        title="Apri profilo utente"
                        aria-label="Apri profilo utente"
                      >
                        <span class="material-icons">visibility</span>
                        <span class="public-bookings-mobile-only">Apri profilo</span>
                      </button>
                      <span v-else class="muted">-</span>
                    </div>
                  </td>
                  <td v-if="canManagePublicCalendar" class="public-booking-actions-cell" data-label="Azioni" role="cell">
                    <div class="buttons-container">
                      <button class="staff-button-positive"
                        type="button"
                        title="Modifica prenotazione"
                        aria-label="Modifica prenotazione"
                        :disabled="bookingSaving"
                        @click="editBooking(booking)"
                      >
                        <span class="material-icons">edit_calendar</span>
                        <span class="public-bookings-mobile-only">Modifica</span>
                      </button>
                      <button class="staff-button-positive"
                        type="button"
                        :title="booking.followUpReminderId ? 'Promemoria follow-up gia creato' : 'Crea promemoria follow-up'"
                        :aria-label="booking.followUpReminderId ? 'Promemoria follow-up gia creato' : 'Crea promemoria follow-up'"
                        :disabled="bookingSaving || reminderSaving || isLoading || !!errorMessage"
                        @click="openFollowUpReminder(booking)"
                      >
                        <span class="material-icons">{{ booking.followUpReminderId ? 'notifications_active' : 'alarm_add' }}</span>
                        <span class="public-bookings-mobile-only">Promemoria</span>
                      </button>
                      <button class="staff-button-danger"
                        type="button"
                        title="Cancella prenotazione"
                        aria-label="Cancella prenotazione"
                        :disabled="bookingSaving || isLoading || !!errorMessage"
                        @click="deleteBooking(booking)"
                      >
                        <span class="material-icons">event_busy</span>
                        <span class="public-bookings-mobile-only">Cancella</span>
                      </button>
                    </div>
                  </td>
                </tr>
                </template>
              </tbody>
            </table>
          </div>

          <div v-else-if="dashboardLoaded && !isLoading && !errorMessage" class="empty-state muted">
            Nessuna prenotazione trovata.
          </div>
          </template>

          <PublicBookingBlockDialog
            :open="blockDialogOpen"
            :initial-date="selectedDate || todayDateKey"
            :saving="blockSaving"
            :error="blockError"
            :conflicts="blockConflicts"
            @close="closeBookingBlock"
            @change="clearBookingBlockFeedback"
            @submit="createBookingBlock"
          />

          <teleport to="body">
            <transition name="modal-fade">
              <div
                v-if="editingBookingId"
                class="booking-modal-backdrop public-bookings-mobile-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="booking-edit-title"
                @click.self="resetBookingEdit"
              >
                <div class="booking-modal-card">
                  <div class="booking-modal-head">
                    <div>
                      <span class="modal-kicker">
                        <span class="material-icons" aria-hidden="true">edit_calendar</span>
                        Modifica prenotazione
                      </span>
                      <h2 id="booking-edit-title">Modifica prenotazione</h2>
                      <div class="booking-modal-subject">
                        {{ editingBookingLabel }}
                      </div>
                      <p>Aggiorna i dettagli dell’appuntamento.</p>
                    </div>
                    <button aria-label="Chiudi finestra" class="modal-close-btn staff-button-neutral" type="button" :disabled="bookingSaving" @click="resetBookingEdit">
                      <span class="material-icons" aria-hidden="true">close</span>
                    </button>
                  </div>

                  <form class="booking-edit-form" @submit.prevent="saveBookingEdit">
                    <div class="toolbar-group booking-meeting-mode-group">
                      <label class="toolbar-label" for="booking-edit-meeting-mode">Modalità appuntamento</label>
                      <select
                        id="booking-edit-meeting-mode"
                        v-model="bookingForm.meetingMode"
                        class="input"
                        :disabled="bookingSaving || !inPersonBookingsEnabled || !canEditBookingMeetingMode(editingBooking)"
                      >
                        <option value="online">Online (videochiamata)</option>
                        <option v-if="canEditBookingMeetingMode(editingBooking)" value="in_person">In presenza</option>
                      </select>
                      <small v-if="!canEditBookingMeetingMode(editingBooking)" class="booking-edit-hint">
                        Le prenotazioni dal sito sono sempre online.
                      </small>
                      <small v-else-if="!inPersonBookingsEnabled" class="booking-edit-hint">
                        Gli appuntamenti in presenza saranno disponibili dopo l’attivazione del servizio.
                      </small>
                      <small v-else class="booking-edit-hint">
                        Il cambio di modalità non invia una nuova conferma al cliente.
                      </small>
                    </div>
                    <div class="toolbar-group">
                      <label class="toolbar-label" for="booking-edit-date">Data</label>
                      <input
                        id="booking-edit-date"
                        v-model="bookingForm.dateKey"
                        class="input"
                        type="date"
                        :disabled="bookingSaving"
                        @change="bookingForm.timeKey = ''"
                        required
                      />
                    </div>

                    <div class="toolbar-group">
                      <label class="toolbar-label" for="booking-edit-time">Orario</label>
                      <input
                        id="booking-edit-time"
                        v-model="bookingForm.timeKey"
                        class="input"
                        type="time"
                        step="60"
                        :disabled="bookingSaving || !bookingForm.dateKey"
                        required
                      />
                      <small class="booking-edit-hint">
                        Puoi scegliere anche date e orari non presenti nel calendario pubblico.
                      </small>
                    </div>

                    <div class="toolbar-group booking-note-group">
                      <label class="toolbar-label" for="booking-edit-note">Note prenotazione</label>
                      <textarea
                        id="booking-edit-note"
                        v-model.trim="bookingForm.note"
                        class="input booking-note-input"
                        :disabled="bookingSaving"
                        rows="4"
                        placeholder="Note interne dedicate a questa prenotazione"
                      ></textarea>
                    </div>

                    <div class="booking-modal-actions">
                      <button class="btn ghost staff-button-cancel" type="button" :disabled="bookingSaving" @click="resetBookingEdit">
                        Annulla
                      </button>
                      <button class="btn primary staff-button-positive" type="submit" :disabled="bookingSaving">
                        <span class="material-icons" aria-hidden="true">save</span>
                        {{ bookingSaving ? 'Salvo...' : 'Salva prenotazione' }}
                      </button>
                    </div>
                  </form>

                  <div v-if="bookingMessage || bookingError" class="feedback-row schedule-feedback">
                    <span v-if="bookingMessage" class="feedback-pill success">
                      <span class="material-icons" aria-hidden="true">check_circle</span>
                      {{ bookingMessage }}
                    </span>
                    <span v-if="bookingError" class="feedback-pill danger">
                      <span class="material-icons" aria-hidden="true">error</span>
                      {{ bookingError }}
                    </span>
                  </div>
                </div>
              </div>
            </transition>
          </teleport>

          <teleport to="body">
            <transition name="modal-fade">
              <div
                v-if="manualBookingOpen"
                class="booking-modal-backdrop public-bookings-mobile-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="booking-create-title"
                @click.self="resetManualBooking"
              >
                <div class="booking-modal-card">
                  <div class="booking-modal-head">
                    <div>
                      <span class="modal-kicker">
                        <span class="material-icons" aria-hidden="true">add_circle</span>
                        Nuova prenotazione
                      </span>
                      <h2 id="booking-create-title">Aggiungi prenotazione</h2>
                      <p>Scegli uno slot dal calendario oppure inserisci liberamente giorno e orario.</p>
                    </div>
                    <button aria-label="Chiudi finestra" class="modal-close-btn staff-button-neutral" type="button" :disabled="bookingSaving" @click="resetManualBooking">
                      <span class="material-icons" aria-hidden="true">close</span>
                    </button>
                  </div>

                  <form class="booking-edit-form booking-create-form" @submit.prevent="createManualBooking">
                    <div class="toolbar-group booking-meeting-mode-group">
                      <label class="toolbar-label" for="booking-create-meeting-mode">Modalità appuntamento</label>
                      <select
                        id="booking-create-meeting-mode"
                        v-model="manualBookingForm.meetingMode"
                        class="input"
                        :disabled="bookingSaving"
                        required
                      >
                        <option value="online">Online (videochiamata)</option>
                        <option value="in_person" :disabled="!inPersonBookingsEnabled">In presenza</option>
                      </select>
                      <small v-if="!inPersonBookingsEnabled" class="booking-edit-hint">
                        Gli appuntamenti in presenza saranno disponibili dopo l’attivazione del servizio.
                      </small>
                    </div>
                    <div class="manual-booking-mode-group">
                      <span class="toolbar-label">Come vuoi scegliere data e orario?</span>
                      <div class="manual-booking-mode-switch" role="radiogroup" aria-label="Modalita prenotazione">
                        <button
                          type="button"
                          :class="['manual-booking-mode-option', { active: manualBookingMode === 'calendar' }]"
                          role="radio"
                          :aria-checked="manualBookingMode === 'calendar'"
                          :disabled="bookingSaving"
                          @click="setManualBookingMode('calendar')"
                        >
                          <span class="material-icons" aria-hidden="true">event_available</span>
                          <span>
                            <strong>Da calendario</strong>
                            <small>Orari liberi e occupati</small>
                          </span>
                        </button>
                        <button
                          type="button"
                          :class="['manual-booking-mode-option', { active: manualBookingMode === 'free' }]"
                          role="radio"
                          :aria-checked="manualBookingMode === 'free'"
                          :disabled="bookingSaving"
                          @click="setManualBookingMode('free')"
                        >
                          <span class="material-icons" aria-hidden="true">edit_calendar</span>
                          <span>
                            <strong>Scelta libera</strong>
                            <small>Qualsiasi giorno e orario</small>
                          </span>
                        </button>
                      </div>
                    </div>

                    <div class="toolbar-group">
                      <label class="toolbar-label" for="booking-create-date">Data</label>
                      <select
                        v-if="manualBookingMode === 'calendar'"
                        id="booking-create-date"
                        v-model="manualBookingForm.dateKey"
                        class="input"
                        :disabled="bookingSaving || manualBookingAvailabilityLoading || !manualCalendarDates.length"
                        @change="manualBookingForm.timeKey = ''"
                        required
                      >
                        <option value="" disabled>Seleziona data</option>
                        <option
                          v-for="date in manualCalendarDates"
                          :key="date.dateKey"
                          :value="date.dateKey"
                        >
                          {{ formatDateLabel(date.dateKey) }} · {{ date.availableCount }} {{ date.availableCount === 1 ? 'orario' : 'orari' }}
                        </option>
                      </select>
                      <input
                        v-else
                        id="booking-create-date"
                        v-model="manualBookingForm.dateKey"
                        class="input"
                        type="date"
                        :min="todayDateKey"
                        :disabled="bookingSaving"
                        @change="handleManualBookingFreeDateChange"
                        required
                      />
                      <small
                        v-if="manualBookingMode === 'calendar' && manualBookingAvailabilityLoading"
                        class="booking-edit-hint"
                      >
                        Carico date e orari del calendario...
                      </small>
                      <small
                        v-else-if="manualBookingMode === 'calendar' && manualBookingAvailabilityError"
                        class="booking-edit-hint booking-edit-hint--danger"
                      >
                        {{ manualBookingAvailabilityError }}
                      </small>
                      <small v-else-if="manualBookingMode === 'calendar' && !manualCalendarDates.length" class="booking-edit-hint">
                        Nessuna data configurata con orari selezionabili.
                        <button type="button" class="inline-mode-link staff-button-neutral" @click="setManualBookingMode('free')">
                          Usa scelta libera
                        </button>
                      </small>
                    </div>

                    <div class="toolbar-group">
                      <label class="toolbar-label" for="booking-create-time">Orario</label>
                      <select
                        v-if="manualBookingMode === 'calendar'"
                        id="booking-create-time"
                        v-model="manualBookingForm.timeKey"
                        class="input"
                        :disabled="bookingSaving || !manualBookingForm.dateKey || !manualCalendarTimeOptions.length"
                        required
                      >
                        <option value="" disabled>Seleziona orario</option>
                        <option
                          v-for="slot in manualCalendarTimeOptions"
                          :key="slot.timeKey"
                          :value="slot.timeKey"
                        >
                          {{ slot.timeKey }}{{ slot.available === true ? '' : ' · Occupato' }}
                        </option>
                      </select>
                      <input
                        v-else
                        id="booking-create-time"
                        v-model="manualBookingForm.timeKey"
                        list="booking-create-time-options"
                        type="time"
                        step="60"
                        class="input"
                        :disabled="bookingSaving || !manualBookingForm.dateKey"
                        required
                      />
                      <datalist v-if="manualBookingMode === 'free'" id="booking-create-time-options">
                        <option
                          v-for="slot in manualFreeCalendarSlots"
                          :key="slot.timeKey"
                          :value="slot.timeKey"
                        />
                      </datalist>
                      <small
                        v-if="manualBookingMode === 'calendar' && manualBookingForm.dateKey && !manualCalendarTimeOptions.length"
                        class="booking-edit-hint"
                      >
                        Non ci sono orari selezionabili per questa data.
                      </small>
                    </div>

                    <div
                      v-if="manualFreeCalendarPanelVisible"
                      class="manual-free-calendar-slots"
                    >
                      <div class="manual-free-calendar-head">
                        <span>
                          <strong>Orari del calendario</strong>
                          <small>Puoi selezionare anche un orario già occupato.</small>
                        </span>
                        <span
                          v-if="!manualBookingAvailabilityLoading && !manualBookingAvailabilityError && manualFreeCalendarSlots.length"
                          class="manual-free-calendar-summary"
                          aria-live="polite"
                        >
                          {{ manualFreeCalendarSummary }}
                        </span>
                      </div>

                      <div v-if="manualBookingAvailabilityLoading" class="manual-free-calendar-feedback">
                        Carico gli orari della giornata...
                      </div>
                      <div
                        v-else-if="manualBookingAvailabilityError"
                        class="manual-free-calendar-feedback is-error"
                      >
                        {{ manualBookingAvailabilityError }} Puoi comunque inserire l’orario manualmente.
                      </div>
                      <template v-else>
                        <div
                          v-if="manualFreeCalendarSlots.length"
                          class="manual-free-slot-grid"
                          aria-label="Orari configurati per la data selezionata"
                        >
                          <button
                            v-for="slot in manualFreeCalendarSlots"
                            :key="slot.timeKey"
                            type="button"
                            :class="[
                              'manual-free-slot',
                              slot.available === true ? 'is-available' : 'is-unavailable',
                              { 'is-selected': manualBookingForm.timeKey === slot.timeKey },
                            ]"
                            :disabled="bookingSaving"
                            :aria-label="`${slot.timeKey}, ${manualFreeSlotStatusLabel(slot)}`"
                            :aria-pressed="manualBookingForm.timeKey === slot.timeKey"
                            @click="selectManualFreeSlot(slot)"
                          >
                            <strong>{{ slot.timeKey }}</strong>
                            <small>{{ manualFreeSlotStatusLabel(slot) }}</small>
                          </button>
                        </div>
                        <div v-else class="manual-free-calendar-feedback">
                          Nessun orario configurato per questa giornata.
                        </div>
                      </template>

                      <small class="manual-free-time-help">
                        Puoi anche scrivere un orario diverso, ad esempio 12:05 o 18:25. Le fasce bloccate restano escluse.
                      </small>
                    </div>

                    <div
                      v-if="manualBookingScheduleInfo"
                      :class="['manual-booking-schedule-info', `is-${manualBookingScheduleInfo.kind}`]"
                      role="status"
                    >
                      <span class="material-icons" aria-hidden="true">
                        {{ manualBookingScheduleInfo.icon }}
                      </span>
                      <span>
                        <strong>{{ manualBookingScheduleInfo.label }}</strong>
                        <small>{{ manualBookingScheduleInfo.message }}</small>
                      </span>
                    </div>

                    <div class="toolbar-group">
                      <label class="toolbar-label" for="booking-create-first-name">Nome</label>
                      <input
                        id="booking-create-first-name"
                        v-model.trim="manualBookingForm.firstName"
                        class="input"
                        type="text"
                        :disabled="bookingSaving || manualUserCreateLoading"
                        required
                      />
                    </div>

                    <div class="toolbar-group">
                      <label class="toolbar-label" for="booking-create-last-name">Cognome</label>
                      <input
                        id="booking-create-last-name"
                        v-model.trim="manualBookingForm.lastName"
                        class="input"
                        type="text"
                        :disabled="bookingSaving || manualUserCreateLoading"
                        required
                      />
                    </div>

                    <div class="toolbar-group email-lookup-group">
                      <label class="toolbar-label" for="booking-create-email">Email</label>
                      <div class="email-lookup-row">
                        <input
                          id="booking-create-email"
                          v-model.trim="manualBookingForm.email"
                          class="input"
                          type="email"
                          :disabled="bookingSaving || manualUserLookupLoading || manualUserCreateLoading"
                          required
                          @input="clearManualUserLookup"
                          @keyup.enter.prevent="findManualBookingUserByEmail"
                        />
                        <button
                          class="btn ghost email-search-btn staff-button-neutral"
                          type="button"
                          :disabled="bookingSaving || manualUserLookupLoading || manualUserCreateLoading || !manualBookingForm.email"
                          @click="findManualBookingUserByEmail"
                        >
                          {{ manualUserLookupLoading ? 'Cerco...' : 'Cerca' }}
                        </button>
                      </div>
                      <small
                        v-if="manualUserLookupMessage || manualUserLookupError"
                        :class="['lookup-feedback', manualUserLookupError ? 'danger' : 'success']"
                      >
                        {{ manualUserLookupError || manualUserLookupMessage }}
                      </small>
                      <div v-if="manualUserLookupCanCreate" class="lookup-actions">
                        <button
                          class="btn ghost create-user-btn staff-button-positive"
                          type="button"
                          :disabled="bookingSaving || manualUserCreateLoading"
                          @click="createManualUserFromButton"
                        >
                          <span class="material-icons" aria-hidden="true">person_add</span>
                          {{ manualUserCreateLoading ? 'Creo...' : 'Crea utente' }}
                        </button>
                      </div>
                    </div>

                    <div class="toolbar-group">
                      <label class="toolbar-label" for="booking-create-phone">Telefono opzionale</label>
                      <input
                        id="booking-create-phone"
                        v-model.trim="manualBookingForm.phone"
                        class="input"
                        type="tel"
                        :disabled="bookingSaving || manualUserCreateLoading"
                      />
                    </div>

                    <div class="toolbar-group booking-note-group">
                      <label class="toolbar-label" for="booking-create-note">Note prenotazione</label>
                      <textarea
                        id="booking-create-note"
                        v-model.trim="manualBookingForm.note"
                        class="input booking-note-input"
                        :disabled="bookingSaving || manualUserCreateLoading"
                        rows="4"
                        placeholder="Note interne dedicate a questa prenotazione"
                      ></textarea>
                    </div>

                    <div class="booking-modal-actions">
                      <button class="btn ghost staff-button-cancel" type="button" :disabled="bookingSaving" @click="resetManualBooking">
                        Annulla
                      </button>
                      <button class="staff-button-positive"
                        :class="['btn', 'primary', 'manual-booking-submit', { 'is-internal': manualBookingIsInternal }]"
                        type="submit"
                        :disabled="bookingSaving || manualUserLookupLoading || manualUserCreateLoading || manualBookingAvailabilityLoading || manualBookingScheduleBlocked"
                      >
                        <span class="material-icons" aria-hidden="true">save</span>
                        {{ bookingSaving ? 'Creo...' : manualBookingSubmitLabel }}
                      </button>
                    </div>
                  </form>

                  <div v-if="bookingMessage || bookingError" class="feedback-row schedule-feedback">
                    <span v-if="bookingMessage" class="feedback-pill success">
                      <span class="material-icons" aria-hidden="true">check_circle</span>
                      {{ bookingMessage }}
                    </span>
                    <span v-if="bookingError" class="feedback-pill danger">
                      <span class="material-icons" aria-hidden="true">error</span>
                      {{ bookingError }}
                    </span>
                  </div>
                </div>
              </div>
            </transition>
          </teleport>

          <teleport to="body">
            <transition name="modal-fade">
              <div
                v-if="reminderModalOpen"
                class="booking-modal-backdrop public-bookings-mobile-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="booking-reminder-title"
                @click.self="resetFollowUpReminder"
              >
                <div class="booking-modal-card booking-modal-card--wide">
                  <div class="booking-modal-head">
                    <div>
                      <span class="modal-kicker">
                        <span class="material-icons" aria-hidden="true">alarm_add</span>
                        Promemoria follow-up
                      </span>
                      <h2 id="booking-reminder-title">Ricontatto dopo l’appuntamento</h2>
                      <div class="booking-modal-subject">
                        {{ reminderBookingLabel }}
                      </div>
                      <p>Salva un promemoria in dashboard per ricordarti di ricontattare la cliente.</p>
                    </div>
                    <button aria-label="Chiudi finestra" class="modal-close-btn staff-button-neutral" type="button" :disabled="reminderSaving" @click="resetFollowUpReminder">
                      <span class="material-icons" aria-hidden="true">close</span>
                    </button>
                  </div>

                  <form class="booking-edit-form follow-up-reminder-form" @submit.prevent="createFollowUpReminder">
                    <div class="toolbar-group">
                      <label class="toolbar-label" for="booking-reminder-date">Data e ora</label>
                      <input
                        id="booking-reminder-date"
                        v-model="reminderForm.data"
                        class="input"
                        type="datetime-local"
                        :disabled="reminderSaving"
                        required
                      />
                    </div>

                    <div class="toolbar-group">
                      <label class="toolbar-label" for="booking-reminder-assigned">Assegnato a</label>
                      <input
                        id="booking-reminder-assigned"
                        v-model.trim="reminderForm.assignedEmail"
                        class="input"
                        type="text"
                        :disabled="reminderSaving"
                        required
                      />
                    </div>

                    <div class="toolbar-group">
                      <label class="toolbar-label" for="booking-reminder-type">Tipo</label>
                      <select
                        id="booking-reminder-type"
                        v-model="reminderForm.tipo"
                        class="input"
                        :disabled="reminderSaving"
                      >
                        <option value="telefonata">Chiamata</option>
                        <option value="whatsapp">Whatsapp</option>
                        <option value="email">E-mail</option>
                        <option value="messaggio">Messaggio</option>
                        <option value="ricontatto finale">Ricontatto finale</option>
                        <option value="altro">Altro</option>
                      </select>
                    </div>

                    <div class="toolbar-group">
                      <label class="toolbar-label" for="booking-reminder-urgency">Urgenza</label>
                      <select
                        id="booking-reminder-urgency"
                        v-model="reminderForm.urgenza"
                        class="input"
                        :disabled="reminderSaving"
                      >
                        <option value="bassa">Bassa</option>
                        <option value="media">Media</option>
                        <option value="alta">Alta</option>
                      </select>
                    </div>

                    <div class="toolbar-group follow-up-note-group">
                      <label class="toolbar-label" for="booking-reminder-note">Note</label>
                      <textarea
                        id="booking-reminder-note"
                        v-model.trim="reminderForm.note"
                        class="input follow-up-note"
                        :disabled="reminderSaving"
                        rows="5"
                      ></textarea>
                    </div>

                    <div class="booking-modal-actions">
                      <button class="btn ghost staff-button-cancel" type="button" :disabled="reminderSaving" @click="resetFollowUpReminder">
                        Annulla
                      </button>
                      <button class="btn primary staff-button-positive" type="submit" :disabled="reminderSaving">
                        <span class="material-icons" aria-hidden="true">save</span>
                        {{ reminderSaving ? 'Salvo...' : 'Crea promemoria' }}
                      </button>
                    </div>
                  </form>

                  <div v-if="reminderMessage || reminderError" class="feedback-row schedule-feedback">
                    <span v-if="reminderMessage" class="feedback-pill success">
                      <span class="material-icons" aria-hidden="true">check_circle</span>
                      {{ reminderMessage }}
                    </span>
                    <span v-if="reminderError" class="feedback-pill danger">
                      <span class="material-icons" aria-hidden="true">error</span>
                      {{ reminderError }}
                    </span>
                  </div>
                </div>
              </div>
            </transition>
          </teleport>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  addDoc,
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from '../../firebaseConfig';
import {
  commercialCustomerKey,
  createPublicBookingCommercialLoader,
} from '../../lib/publicBookingCommercialLoader';
import { createPublicBookingDashboardLoader } from '../../lib/publicBookingDashboardLoader';
import { bookingPurchaseOutcome } from '../../lib/publicBookingPurchaseOutcome';
import {
  OUT_OF_SCHEDULE_DURATION_MINUTES,
  publicBookingBlocks,
  overlapsPublicBookingBlock,
  publicBookingDuration,
  currentPublicBookingRows,
} from '../../lib/publicBookingBlocks';
import {
  getCreatePublicBookingAdminFunctionUrl,
  getPublicBookingAvailabilityAdminFunctionUrl,
  getUpdatePublicBookingAdminFunctionUrl,
  getUpdatePublicBookingAttendanceAdminFunctionUrl,
  getUpdatePublicBookingDateAdminFunctionUrl,
  getPublicBookingCommercialStatusAdminFunctionUrl,
  getPublicBookingDashboardAdminFunctionUrl,
  getCreatePublicBookingBlockAdminFunctionUrl,
  getRemovePublicBookingBlockAdminFunctionUrl,
} from '../../lib/env';
import SidebarComponent from '../SidebarComponent.vue';
import PublicBookingAttendance from './PublicBookingAttendance.vue';
import PublicBookingNote from './PublicBookingNote.vue';
import PublicBookingsTimeline from './PublicBookingsTimeline.vue';
import PublicBookingBlockDialog from './PublicBookingBlockDialog.vue';

const SERVICES_COLLECTION = 'public_booking_services';
const SERVICE_ID = 'consulenza';
const SERVICE_TIMEZONE = 'Europe/Rome';
const BOOKINGS_COLLECTION = 'public_bookings';
const SLOTS_COLLECTION = 'public_booking_slots';
const LOCKS_COLLECTION = 'public_booking_user_locks';
const REMINDERS_COLLECTION = 'comunicazioni';
const defaultDateForm = () => ({
  dateKey: '',
  start: '09:00',
  end: '17:00',
  slotMinutes: 20,
  visibility: 'public',
});

const defaultBookingForm = () => ({
  dateKey: '',
  timeKey: '',
  note: '',
  meetingMode: 'online',
});

const defaultManualBookingForm = () => ({
  dateKey: '',
  timeKey: '',
  meetingMode: 'online',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  note: '',
  userId: '',
});

const defaultFollowUpReminderForm = () => ({
  data: '',
  tipo: 'whatsapp',
  urgenza: 'media',
  note: '',
  assignedEmail: '',
});

const monthOptions = [
  { value: '01', label: 'Gennaio' },
  { value: '02', label: 'Febbraio' },
  { value: '03', label: 'Marzo' },
  { value: '04', label: 'Aprile' },
  { value: '05', label: 'Maggio' },
  { value: '06', label: 'Giugno' },
  { value: '07', label: 'Luglio' },
  { value: '08', label: 'Agosto' },
  { value: '09', label: 'Settembre' },
  { value: '10', label: 'Ottobre' },
  { value: '11', label: 'Novembre' },
  { value: '12', label: 'Dicembre' },
];

const currentMonthParts = () => {
  const now = new Date();
  return {
    month: String(now.getMonth() + 1).padStart(2, '0'),
    year: String(now.getFullYear()),
  };
};

export default {
  name: 'PublicBookingsList',
  components: {
    SidebarComponent,
    PublicBookingAttendance,
    PublicBookingNote,
    PublicBookingsTimeline,
    PublicBookingBlockDialog,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const bookings = ref([]);
    const bookingView = ref('current');
    const timelineVisibleBookings = ref([]);
    const attendanceSaving = ref({});
    const attendancePending = ref({});
    const attendanceErrors = ref({});
    const attendancePatches = new Map();
    let disposed = false;
    const selectedDate = ref('');
    const selectedMeetingMode = ref('');
    const isLoading = ref(false);
    const dashboardLoaded = ref(false);
    const historyRequested = ref(false);
    const historyLoaded = ref(false);
    const inPersonBookingsEnabled = ref(false);
    const errorMessage = ref('');
    const filtersOpen = ref(false);
    const pastDatesOpen = ref(false);
    const scheduleEditorOpen = ref(false);
    const availableDatesOpen = ref(false);
    const currentConfigMonth = currentMonthParts();
    const selectedConfigMonth = ref(currentConfigMonth.month);
    const selectedConfigYear = ref(currentConfigMonth.year);
    const serviceConfig = ref(null);
    const serviceLoading = ref(false);
    const serviceError = ref('');
    const scheduleSaving = ref(false);
    const scheduleMessage = ref('');
    const scheduleError = ref('');
    const editingDateKey = ref('');
    const dateForm = ref(defaultDateForm());
    const editingBookingId = ref('');
    const editingBooking = ref(null);
    const bookingForm = ref(defaultBookingForm());
    const bookingSaving = ref(false);
    const bookingMessage = ref('');
    const bookingError = ref('');
    const blockDialogOpen = ref(false);
    const blockSaving = ref(false);
    const blockError = ref('');
    const blockConflicts = ref([]);
    const bookingBlocks = computed(() => publicBookingBlocks(serviceConfig.value));
    const manualBookingOpen = ref(false);
    const manualBookingMode = ref('calendar');
    const manualBookingForm = ref(defaultManualBookingForm());
    const manualBookingAvailability = ref([]);
    const manualBookingAvailabilityLoading = ref(false);
    const manualBookingAvailabilityError = ref('');
    const manualUserLookupLoading = ref(false);
    const manualUserCreateLoading = ref(false);
    const manualUserLookupMessage = ref('');
    const manualUserLookupError = ref('');
    const manualUserLookupCanCreate = ref(false);
    const reminderModalOpen = ref(false);
    const reminderBooking = ref(null);
    const reminderForm = ref(defaultFollowUpReminderForm());
    const reminderSaving = ref(false);
    const reminderMessage = ref('');
    const reminderError = ref('');
    const commercialStatusByBookingId = ref({});
    const currentAuthUser = ref(auth.currentUser || null);
    const canViewPublicCalendar = computed(() => (
      currentAuthUser.value?.uid === 'HpMiFyBZnwX6EUf6VR9iCsHkqo82'
      && String(currentAuthUser.value?.email || '').trim().toLowerCase() === 'valeriobianconi@gmail.com'
    ));
    let unsubscribeAuth = null;
    let commercialStatusRefreshRequested = false;
    let manualBookingAvailabilityRequestId = 0;
    let manualBookingDeepLinkHandled = false;

    const toDate = (value) => {
      if (!value) return null;
      if (typeof value.toDate === 'function') return value.toDate();
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    };

    const formatDateKeyFromDate = (date) => {
      if (!date) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const currentServiceDateTimeParts = () => {
      const formatInTimeZone = (timeZone) => new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      }).formatToParts(new Date()).reduce((parts, part) => {
        if (part.type !== 'literal') parts[part.type] = part.value;
        return parts;
      }, {});

      let parts;
      try {
        parts = formatInTimeZone(serviceConfig.value?.timezone || SERVICE_TIMEZONE);
      } catch (error) {
        console.warn('Timezone booking non valida, uso Europe/Rome:', error);
        parts = formatInTimeZone(SERVICE_TIMEZONE);
      }
      return parts;
    };

    const currentServiceDateKey = () => {
      const parts = currentServiceDateTimeParts();
      return `${parts.year}-${parts.month}-${parts.day}`;
    };

    const currentServiceTimeMinutes = () => {
      const parts = currentServiceDateTimeParts();
      return Number(parts.hour) * 60 + Number(parts.minute);
    };

    const formatTimeFromDate = (date) => {
      if (!date) return '';
      return date.toLocaleTimeString('it-IT', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    };

    const normalizeBooking = (docSnap) => {
      const data = docSnap.data() || {};
      const startAtDate = toDate(data.startAt);
      const createdAtDate = toDate(data.createdAt);
      const firstName = String(data.firstName || data.name || '').trim();
      const lastName = String(data.lastName || data.surname || '').trim();

      return {
        id: data.id || docSnap.id,
        serviceId: data.serviceId || '',
        serviceName: data.serviceName || '',
        userId: data.userId || data.uid || '',
        email: data.email || '',
        firstName,
        lastName,
        fullName: `${firstName} ${lastName}`.trim(),
        phone: data.phone || '',
        note: String(data.note ?? data.notes ?? data.publicBookingNote ?? '').trim(),
        dateKey: data.dateKey || formatDateKeyFromDate(startAtDate),
        timeKey: data.timeKey || formatTimeFromDate(startAtDate),
        timezone: data.timezone || 'Europe/Rome',
        status: data.status || '',
        participated: typeof data.participated === 'boolean' ? data.participated : null,
        participatedUpdatedAt: data.participatedUpdatedAt || null,
        source: data.source || '',
        meetingMode: data.meetingMode === 'in_person' ? 'in_person' : 'online',
        outOfSchedule: data.outOfSchedule === true,
        slotId: data.slotId || '',
        followUpReminderId: data.followUpReminderId || '',
        startAtDate,
        createdAtDate,
      };
    };

    const sortBookings = (items = []) => [...items].sort((a, b) => {
      const left = `${a.dateKey || ''} ${a.timeKey || ''}`;
      const right = `${b.dateKey || ''} ${b.timeKey || ''}`;
      return left.localeCompare(right);
    });

    const applyCreatedBooking = (payload) => {
      const item = payload?.booking;
      if (
        !item || typeof item.id !== 'string' || !item.id
        || (payload.bookingId && payload.bookingId !== item.id)
        || !item.data || typeof item.data !== 'object' || Array.isArray(item.data)
      ) return false;
      const savedBooking = normalizeBooking({
        id: item.id,
        data: () => ({ ...item.data, id: item.id }),
      });
      bookings.value = sortBookings([
        ...bookings.value.filter((booking) => booking.id !== savedBooking.id),
        savedBooking,
      ]);
      return true;
    };

    const serviceRef = (db) => doc(db, SERVICES_COLLECTION, SERVICE_ID);

    const requestDashboard = async ({ signal }) => {
      const scope = historyRequested.value ? 'all' : 'upcoming';
      const user = auth.currentUser;
      if (!user) throw new Error('Accesso richiesto. Accedi nuovamente per caricare le prenotazioni.');
      const token = await user.getIdToken();
      if (signal.aborted) throw new Error('Caricamento interrotto.');
      const endpoint = new URL(getPublicBookingDashboardAdminFunctionUrl(), window.location.origin);
      endpoint.searchParams.set('service', SERVICE_ID);
      endpoint.searchParams.set('scope', scope);
      const response = await fetch(endpoint.toString(), {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store',
        signal,
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload?.error || 'Non riesco a caricare le prenotazioni pubbliche.');
      if (
        payload?.ok !== true
        || !Array.isArray(payload.bookings)
        || !payload.serviceConfig
        || typeof payload.serviceConfig !== 'object'
        || Array.isArray(payload.serviceConfig)
        || (payload.bookingScope != null && !['upcoming', 'all'].includes(payload.bookingScope))
        || payload.bookings.some((item) => !item || typeof item.id !== 'string' || !item.id
          || !item.data || typeof item.data !== 'object' || Array.isArray(item.data))
      ) throw new Error('La risposta delle prenotazioni non è valida. Riprova il caricamento.');
      return payload;
    };

    const applyDashboardSnapshot = (payload) => {
      const nextBookings = sortBookings(payload.bookings.map((item) => normalizeBooking({
        id: item.id,
        data: () => ({ ...item.data, id: item.id }),
      })));
      serviceConfig.value = payload.serviceConfig;
      // Older deployed endpoints return the complete collection without scope metadata.
      historyLoaded.value = payload.bookingScope !== 'upcoming';
      if (historyLoaded.value) historyRequested.value = true;
      inPersonBookingsEnabled.value = Array.isArray(payload.bookingCapabilities?.meetingModes)
        && payload.bookingCapabilities.meetingModes.includes('in_person');
      refreshTodayDateKey();
      if (/^\d{4}-\d{2}-\d{2}$/.test(payload.todayDateKey || '')) {
        todayDateKey.value = payload.todayDateKey;
      }
      // The watcher refreshes only the visible customers, preserving the selected day.
      commercialStatusRefreshRequested = true;
      bookings.value = nextBookings.map((booking) => {
        const patch = attendancePatches.get(booking.id);
        // A dashboard read started before a save must not restore the old flag.
        if (patch && (!booking.participatedUpdatedAt
          || new Date(patch.participatedUpdatedAt) > new Date(booking.participatedUpdatedAt))) {
          return { ...booking, ...patch };
        }
        attendancePatches.delete(booking.id);
        return booking;
      });
      dashboardLoaded.value = true;
    };

    const dashboardLoader = createPublicBookingDashboardLoader({
      request: requestDashboard,
      onData: applyDashboardSnapshot,
      onLoading: (loading) => {
        isLoading.value = loading;
        serviceLoading.value = loading;
        if (loading) {
          errorMessage.value = '';
          serviceError.value = '';
        }
      },
      onError: (error) => {
        console.error('Errore caricamento prenotazioni pubbliche:', error);
        const message = error.code === 'dashboard-timeout'
          ? 'Il caricamento sta impiegando troppo tempo. Riprova.'
          : error.message || 'Non riesco a caricare le prenotazioni pubbliche. Riprova.';
        errorMessage.value = dashboardLoaded.value
          ? `Elenco non aggiornato. ${message} I dati già caricati restano visibili.`
          : message;
      },
    });
    const refreshDashboard = (options) => dashboardLoader.refresh(options);
    const fetchBookings = () => refreshDashboard();
    const fetchServiceConfig = () => refreshDashboard();

    const ensureBookingHistory = () => {
      if (historyLoaded.value) return Promise.resolve(true);
      const expandsScope = !historyRequested.value;
      historyRequested.value = true;
      // An in-flight upcoming request cannot satisfy a request for the history.
      return refreshDashboard({ force: expandsScope });
    };

    const openTimeline = () => {
      bookingView.value = 'timeline';
      void ensureBookingHistory();
    };

    const saveAttendance = async (booking, participated) => {
      if (!booking?.id || (participated !== null && typeof participated !== 'boolean')
        || booking.participated === participated || attendanceSaving.value[booking.id]
        || isLoading.value || !canManagePublicCalendar.value) return;
      const user = auth.currentUser;
      if (!user) return;
      attendanceSaving.value = { ...attendanceSaving.value, [booking.id]: true };
      attendancePending.value = { ...attendancePending.value, [booking.id]: participated };
      attendanceErrors.value = { ...attendanceErrors.value, [booking.id]: '' };
      bookingError.value = '';
      bookingMessage.value = '';
      const controller = new AbortController();
      let timer;
      const timeout = new Promise((resolve, reject) => {
        timer = setTimeout(() => {
          controller.abort();
          reject(Object.assign(new Error('Tempo di attesa superato.'), { name: 'AbortError' }));
        }, 25000);
      });
      try {
        const payload = await Promise.race([(async () => {
          const token = await user.getIdToken();
          if (disposed || controller.signal.aborted || auth.currentUser?.uid !== user.uid) return null;
          const response = await fetch(getUpdatePublicBookingAttendanceAdminFunctionUrl(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ bookingId: booking.id, participated }),
            signal: controller.signal,
          });
          const result = await response.json().catch(() => ({}));
          if (!response.ok || result.ok !== true || result.bookingId !== booking.id
            || result.participated !== participated || !result.participatedUpdatedAt) {
            throw new Error(result.error || 'Non riesco a salvare la presenza. Riprova.');
          }
          return result;
        })(), timeout]);
        if (!payload || disposed || auth.currentUser?.uid !== user.uid) return;
        const patch = { participated: payload.participated, participatedUpdatedAt: payload.participatedUpdatedAt };
        attendancePatches.set(booking.id, patch);
        bookings.value = bookings.value.map((item) => item.id === booking.id ? { ...item, ...patch } : item);
        const confirmation = participated === null
          ? 'Partecipazione riportata a Da verificare'
          : `${participated ? 'Presenza' : 'Assenza'} registrata`;
        bookingMessage.value = `${confirmation} per ${booking.fullName || booking.email || 'la prenotazione'}.`;
      } catch (error) {
        if (!disposed && auth.currentUser?.uid === user.uid) {
          bookingError.value = error.name === 'AbortError'
            ? 'Conferma del salvataggio non ricevuta. Aggiorna la lista per verificare la presenza prima di riprovare.'
            : error.message || 'Salvataggio della presenza non riuscito.';
          attendanceErrors.value = { ...attendanceErrors.value, [booking.id]: bookingError.value };
        }
      } finally {
        clearTimeout(timer);
        if (!disposed && auth.currentUser?.uid === user.uid) {
          const next = { ...attendanceSaving.value };
          delete next[booking.id];
          attendanceSaving.value = next;
          const pending = { ...attendancePending.value };
          delete pending[booking.id];
          attendancePending.value = pending;
        }
      }
    };

    const minutesFromTime = (value) => {
      const match = String(value || '').match(/^(\d{1,2}):(\d{2})$/);
      if (!match) return null;
      const hours = Number(match[1]);
      const minutes = Number(match[2]);
      if (
        !Number.isInteger(hours)
        || !Number.isInteger(minutes)
        || hours < 0
        || hours > 23
        || minutes < 0
        || minutes > 59
      ) {
        return null;
      }
      return hours * 60 + minutes;
    };

    const addDaysToDateKey = (dateKey, days) => {
      const date = new Date(`${dateKey}T12:00:00`);
      if (Number.isNaN(date.getTime())) return dateKey;
      date.setDate(date.getDate() + days);
      return formatDateKeyFromDate(date);
    };

    const todayDateKey = ref(currentServiceDateKey());

    const refreshTodayDateKey = () => {
      todayDateKey.value = currentServiceDateKey();
    };

    const bookingHorizonDays = computed(() => {
      const value = Number(serviceConfig.value?.bookingHorizonDays || 45);
      return Number.isFinite(value) && value > 0 ? Math.min(90, Math.round(value)) : 45;
    });

    const lastVisibleDateKey = computed(() => addDaysToDateKey(todayDateKey.value, bookingHorizonDays.value - 1));

    const isDateWithinHorizon = (dateKey) => (
      dateKey >= todayDateKey.value && dateKey <= lastVisibleDateKey.value
    );

    const normalizeRange = (range = {}) => ({
      start: String(range.start || '09:00').slice(0, 5),
      end: String(range.end || '17:00').slice(0, 5),
      slotMinutes: Math.max(5, Number(range.slotMinutes || serviceConfig.value?.slotMinutes || 20)),
      visibility: String(range.visibility || 'public').toLowerCase() === 'hidden' ? 'hidden' : 'public',
    });

    const normalizeDateOverride = (dateKey, override) => {
      let visibility = 'public';
      let ranges = [];

      if (Array.isArray(override)) {
        ranges = override;
      } else if (override && typeof override === 'object') {
        visibility = String(override.visibility || 'public').toLowerCase() === 'hidden' ? 'hidden' : 'public';
        ranges = Array.isArray(override.ranges)
          ? override.ranges
          : (Array.isArray(override.slots) ? override.slots : []);
      }

      const normalizedRanges = ranges.map(normalizeRange).filter((range) => (
        minutesFromTime(range.start) != null
        && minutesFromTime(range.end) != null
        && minutesFromTime(range.end) > minutesFromTime(range.start)
      ));
      const primaryRange = normalizedRanges[0] || normalizeRange();
      const rangeVisible = normalizedRanges.some((range) => range.visibility !== 'hidden');
      const bookingCount = !dashboardLoaded.value || (dateKey < todayDateKey.value && !historyLoaded.value)
        ? null : bookings.value.filter((booking) => (
        booking.dateKey === dateKey
        && booking.status !== 'cancelled'
        && booking.status !== 'canceled'
      )).length;
      const closedDates = Array.isArray(serviceConfig.value?.closedDates) ? serviceConfig.value.closedDates : [];
      const isClosed = closedDates.includes(dateKey);
      const withinHorizon = isDateWithinHorizon(dateKey);
      const isPublicNow = visibility === 'public' && rangeVisible && withinHorizon && !isClosed;
      let publicLabel = 'Visibile';
      if (isClosed) publicLabel = 'Chiusa';
      else if (!withinHorizon) publicLabel = 'Fuori finestra';
      else if (visibility !== 'public' || !rangeVisible) publicLabel = 'Nascosta';

      return {
        dateKey,
        visibility,
        ranges: normalizedRanges.length ? normalizedRanges : [primaryRange],
        start: primaryRange.start,
        end: primaryRange.end,
        slotMinutes: Math.round(primaryRange.slotMinutes),
        bookingCount,
        withinHorizon,
        isPublicNow,
        publicLabel,
      };
    };

    const availableDates = computed(() => (
      [...new Set([...bookings.value
        .filter((booking) => booking.status !== 'cancelled' && booking.status !== 'canceled')
        .map((booking) => booking.dateKey)
        .filter(Boolean), ...bookingBlocks.value.map(block => block.dateKey)])].sort()
    ));

    const currentAndFutureAvailableDates = computed(() => (
      availableDates.value.filter((dateKey) => dateKey >= todayDateKey.value)
    ));

    const pastAvailableDates = computed(() => (
      availableDates.value
        .filter((dateKey) => dateKey < todayDateKey.value)
        .sort((a, b) => b.localeCompare(a))
    ));

    const configuredDates = computed(() => {
      const overrides = serviceConfig.value?.dateOverrides || {};
      if (!overrides || typeof overrides !== 'object' || Array.isArray(overrides)) return [];
      return Object.entries(overrides)
        .filter(([dateKey]) => /^\d{4}-\d{2}-\d{2}$/.test(dateKey))
        .map(([dateKey, override]) => normalizeDateOverride(dateKey, override))
        .sort((a, b) => a.dateKey.localeCompare(b.dateKey));
    });
    const configuredDateKeySet = computed(() => new Set(
      configuredDates.value.map((date) => date.dateKey)
    ));

    const configYearOptions = computed(() => {
      const currentYear = new Date().getFullYear();
      const years = new Set([
        String(currentYear - 1),
        String(currentYear),
        String(currentYear + 1),
        String(currentYear + 2),
      ]);
      const selectedYear = String(selectedConfigYear.value || '').trim();
      if (/^\d{4}$/.test(selectedYear)) years.add(selectedYear);
      configuredDates.value.forEach((date) => {
        const year = String(date.dateKey || '').slice(0, 4);
        if (/^\d{4}$/.test(year)) years.add(year);
      });
      return [...years].sort();
    });

    const monthFilteredConfiguredDates = computed(() => {
      const month = String(selectedConfigMonth.value || '').trim().padStart(2, '0');
      const year = String(selectedConfigYear.value || '').trim();
      if (!/^\d{4}$/.test(year) || !/^\d{2}$/.test(month)) return configuredDates.value;
      return configuredDates.value.filter((date) => date.dateKey.startsWith(`${year}-${month}-`));
    });

    const filteredBookings = computed(() => {
      const dateFilter = String(selectedDate.value || '').trim();
      const modeFilter = selectedMeetingMode.value;
      return bookings.value.filter((booking) => {
        const matchesDate = dateFilter
          ? booking.dateKey === dateFilter
          : String(booking.dateKey || '') >= todayDateKey.value;
        const meetingMode = booking.meetingMode === 'in_person' ? 'in_person' : 'online';
        return matchesDate && (!modeFilter || meetingMode === modeFilter);
      });
    });

    const currentBookingRows = computed(() => currentPublicBookingRows(
      filteredBookings.value, bookingBlocks.value, selectedDate.value, todayDateKey.value
    ));

    const isTimeBlocked = (dateKey, timeKey, duration) => overlapsPublicBookingBlock(
      serviceConfig.value, dateKey, timeKey,
      duration || publicBookingDuration(serviceConfig.value, dateKey, timeKey)
    );

    const toggleMeetingModeFilter = (mode) => {
      selectedMeetingMode.value = selectedMeetingMode.value === mode ? '' : mode;
    };

    const needsBookingHistory = computed(() => (
      pastDatesOpen.value
      || (Boolean(selectedDate.value) && selectedDate.value < todayDateKey.value)
      || (canViewPublicCalendar.value && availableDatesOpen.value
        && monthFilteredConfiguredDates.value.some((date) => date.dateKey < todayDateKey.value))
    ));

    watch(needsBookingHistory, (needed) => {
      if (needed) void ensureBookingHistory();
    });

    const editingBookingLabel = computed(() => {
      if (!editingBooking.value) return '';
      const name = editingBooking.value.fullName || editingBooking.value.email || editingBooking.value.id;
      return `${name} - ${formatDateLabel(editingBooking.value.dateKey)} ${editingBooking.value.timeKey || ''}`;
    });

    const slotIdFor = (serviceId, dateKey, timeKey) => (
      `${normalizeServiceId(serviceId || SERVICE_ID)}_${dateKey}_${String(timeKey || '').replace(':', '')}`
    );

    const lockIdFor = (serviceId, userId, dateKey) => (
      `${normalizeServiceId(serviceId || SERVICE_ID)}_${userId}_${dateKey}`
    );

    const generateAdminSlotsForDate = (dateKey, { includeHidden = false } = {}) => {
      const config = configuredDates.value.find((date) => date.dateKey === dateKey);
      if (!config || (!includeHidden && config.visibility === 'hidden')) return [];
      const serviceId = normalizeServiceId(config.serviceId || SERVICE_ID);
      const slots = [];

      (config.ranges || []).forEach((range) => {
        if (!includeHidden && range.visibility === 'hidden') return;
        const start = minutesFromTime(range.start);
        const end = minutesFromTime(range.end);
        const slotMinutes = Math.max(5, Number(range.slotMinutes || config.slotMinutes || serviceConfig.value?.slotMinutes || 20));
        if (start == null || end == null || end <= start) return;
        for (let minute = start; minute + slotMinutes <= end; minute += slotMinutes) {
          const timeKey = `${String(Math.floor(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`;
          if (isTimeBlocked(dateKey, timeKey, slotMinutes)) continue;
          slots.push({
            dateKey,
            timeKey,
            slotId: slotIdFor(serviceId, dateKey, timeKey),
            slotMinutes,
          });
        }
      });

      return slots;
    };

    const manualBookingClosedDate = computed(() => {
      const dateKey = String(manualBookingForm.value.dateKey || '').trim();
      const closedDates = Array.isArray(serviceConfig.value?.closedDates)
        ? serviceConfig.value.closedDates
        : [];
      return Boolean(dateKey) && closedDates.includes(dateKey);
    });
    const manualBookingConfiguredDate = computed(() => {
      const dateKey = String(manualBookingForm.value.dateKey || '').trim();
      return configuredDates.value.find((date) => date.dateKey === dateKey) || null;
    });
    const manualCalendarDates = computed(() => {
      const closedDates = new Set(
        Array.isArray(serviceConfig.value?.closedDates) ? serviceConfig.value.closedDates : []
      );
      return manualBookingAvailability.value
        .filter((date) => (
          /^\d{4}-\d{2}-\d{2}$/.test(date?.dateKey || '')
          && date.dateKey >= todayDateKey.value
          && !closedDates.has(date.dateKey)
        ))
        .map((date) => {
          const slotsByTime = new Map();
          (Array.isArray(date.slots) ? date.slots : []).forEach((slot) => {
            const timeKey = String(slot?.timeKey || '').trim();
            if (!/^\d{2}:\d{2}$/.test(timeKey)) return;
            if (isTimeBlocked(date.dateKey, timeKey, slot.slotMinutes)) return;
            if (!slotsByTime.has(timeKey)) slotsByTime.set(timeKey, slot);
          });
          const slots = [...slotsByTime.values()]
            .sort((left, right) => left.timeKey.localeCompare(right.timeKey));
          return {
            ...date,
            slots,
            availableCount: slots.length,
          };
        })
        .filter((date) => date.availableCount > 0)
        .sort((left, right) => left.dateKey.localeCompare(right.dateKey));
    });
    const manualCalendarTimeOptions = computed(() => {
      const dateKey = String(manualBookingForm.value.dateKey || '').trim();
      return manualCalendarDates.value.find((date) => date.dateKey === dateKey)?.slots || [];
    });
    const manualFreeCalendarDate = computed(() => {
      if (manualBookingMode.value !== 'free') return null;
      const dateKey = String(manualBookingForm.value.dateKey || '').trim();
      if (!dateKey) return null;
      return manualBookingAvailability.value.find((date) => date?.dateKey === dateKey) || null;
    });
    const manualFreeCalendarSlots = computed(() => {
      const slotsByTime = new Map();
      const slots = Array.isArray(manualFreeCalendarDate.value?.slots)
        ? manualFreeCalendarDate.value.slots
        : [];

      slots.forEach((slot) => {
        const timeKey = String(slot?.timeKey || '').trim();
        if (!/^\d{2}:\d{2}$/.test(timeKey)) return;
        if (isTimeBlocked(manualBookingForm.value.dateKey, timeKey, slot.slotMinutes)) return;
        const current = slotsByTime.get(timeKey);
        if (!current || (current.available === true && slot?.available !== true)) {
          slotsByTime.set(timeKey, { ...slot, timeKey });
        }
      });

      return [...slotsByTime.values()]
        .sort((left, right) => left.timeKey.localeCompare(right.timeKey));
    });
    const manualFreeAvailableSlots = computed(() => (
      manualFreeCalendarSlots.value.filter((slot) => slot.available === true)
    ));
    const manualFreeCalendarSummary = computed(() => {
      const availableCount = manualFreeAvailableSlots.value.length;
      const bookedCount = Math.max(0, manualFreeCalendarSlots.value.length - availableCount);
      return `${availableCount} ${availableCount === 1 ? 'libero' : 'liberi'} · ${bookedCount} ${bookedCount === 1 ? 'prenotato' : 'prenotati'}`;
    });
    const publicBookingServiceVisible = computed(() => {
      const config = serviceConfig.value || {};
      if (String(config.visibility || 'hidden').trim().toLowerCase() !== 'public') return false;
      const now = new Date();
      const visibleFrom = toDate(config.visibleFrom);
      const visibleUntil = toDate(config.visibleUntil);
      if (visibleFrom && now < visibleFrom) return false;
      if (visibleUntil && now > visibleUntil) return false;
      return true;
    });
    const manualConfiguredSlots = computed(() => {
      const dateKey = String(manualBookingForm.value.dateKey || '').trim();
      if (!dateKey || manualBookingClosedDate.value) return [];
      return generateAdminSlotsForDate(dateKey, { includeHidden: true });
    });
    const manualPublicSlots = computed(() => {
      const configuredDate = manualBookingConfiguredDate.value;
      if (
        !publicBookingServiceVisible.value
        || !configuredDate
        || !configuredDate.isPublicNow
        || manualBookingClosedDate.value
      ) return [];
      const dateKey = String(manualBookingForm.value.dateKey || '').trim();
      return generateAdminSlotsForDate(dateKey);
    });
    const manualFreeCalendarPanelVisible = computed(() => (
      manualBookingMode.value === 'free'
      && Boolean(manualBookingForm.value.dateKey)
      && !manualBookingClosedDate.value
      && Boolean(manualFreeCalendarDate.value || manualBookingConfiguredDate.value)
    ));
    const manualFreeSlotStatusLabel = (slot = {}) => {
      if (slot.available !== true) return 'Occupato';
      const capacity = Math.max(1, Number(slot.capacity || 1));
      const availableCount = Math.max(0, Number(slot.availableCount || 0));
      if (capacity > 1) return `${availableCount} ${availableCount === 1 ? 'posto libero' : 'posti liberi'}`;
      return 'Libero';
    };
    const selectManualFreeSlot = (slot = {}) => {
      if (bookingSaving.value) return;
      const timeKey = String(slot.timeKey || '').trim();
      if (!/^\d{2}:\d{2}$/.test(timeKey)) return;
      if (isTimeBlocked(manualBookingForm.value.dateKey, timeKey, slot.slotMinutes)) return;
      manualBookingForm.value.timeKey = timeKey;
      bookingError.value = '';
    };
    const manualFreeSelectedCalendarSlot = computed(() => {
      const timeKey = String(manualBookingForm.value.timeKey || '').trim();
      if (!timeKey) return null;
      const slots = manualBookingMode.value === 'calendar'
        ? manualCalendarTimeOptions.value
        : manualFreeCalendarSlots.value;
      return slots.find((slot) => slot.timeKey === timeKey) || null;
    });
    const manualBookingScheduleInfo = computed(() => {
      const dateKey = String(manualBookingForm.value.dateKey || '').trim();
      const timeKey = String(manualBookingForm.value.timeKey || '').trim();
      if (!dateKey) return null;

      if (manualBookingClosedDate.value) {
        return {
          kind: 'closed',
          icon: 'event_busy',
          label: 'Data chiusa',
          message: 'La giornata e stata chiusa esplicitamente: scegli un’altra data.',
        };
      }
      if (!timeKey) return null;

      if (isTimeBlocked(dateKey, timeKey)) {
        return {
          kind: 'blocked', icon: 'block', label: 'Fascia bloccata',
          message: 'Non sono consentite prenotazioni manuali o online in questa fascia. Scegli un altro orario.',
        };
      }

      if (
        manualFreeSelectedCalendarSlot.value
        && manualFreeSelectedCalendarSlot.value.available !== true
      ) {
        return {
          kind: 'occupied',
          icon: 'event_busy',
          label: 'Orario già occupato',
          message: 'Puoi aggiungere un’altra prenotazione a questo orario.',
        };
      }

      const configuredSlot = manualConfiguredSlots.value.find((slot) => slot.timeKey === timeKey);
      if (configuredSlot) {
        const isPublicNow = manualPublicSlots.value.some((slot) => slot.timeKey === timeKey);
        return {
          kind: isPublicNow ? 'public' : 'scheduled',
          icon: isPublicNow ? 'event_available' : 'event',
          label: isPublicNow ? 'Slot pubblico' : 'Orario di calendario',
          message: isPublicNow
            ? 'Slot previsto dal calendario. Puoi prenotare anche se occupato; il cliente riceverà conferma.'
            : 'Orario gia configurato, ma non visibile al pubblico in questo momento. Il cliente ricevera conferma.',
        };
      }
      if (manualBookingConfiguredDate.value) {
        return {
          kind: 'scheduled',
          icon: 'edit_calendar',
          label: 'Data in calendario',
          message: `L’orario è manuale e durerà ${OUT_OF_SCHEDULE_DURATION_MINUTES} minuti. Data e orario saranno verificati al salvataggio.`,
        };
      }
      return {
        kind: 'internal',
        icon: 'lock',
        label: 'Data fuori calendario',
        message: `Durata ${OUT_OF_SCHEDULE_DURATION_MINUTES} minuti. Questa data non verrà aggiunta al calendario pubblico. Il cliente riceverà automaticamente la conferma.`,
      };
    });
    const manualBookingIsInternal = computed(() => manualBookingScheduleInfo.value?.kind === 'internal');
    const manualBookingScheduleBlocked = computed(() => (
      ['closed', 'blocked'].includes(manualBookingScheduleInfo.value?.kind)
    ));
    const manualBookingSubmitLabel = computed(() => (
      manualBookingIsInternal.value ? 'Crea fuori calendario' : 'Crea prenotazione'
    ));

    const formVisibilityHint = computed(() => {
      const dateKey = dateForm.value.dateKey;
      if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) return '';
      if (dateForm.value.visibility === 'hidden') return 'La data verra salvata ma non sara visibile nel calendario pubblico.';
      if (!isDateWithinHorizon(dateKey)) {
        return `La data e fuori dalla finestra pubblica attuale (${bookingHorizonDays.value} giorni). Sara visibile quando rientra nella finestra.`;
      }
      return '';
    });

    const getSlotBookedCount = (snap) => (
      snap?.exists() ? Math.max(0, Number((snap.data() || {}).bookedCount || 0)) : 0
    );

    const formatDateLabel = (dateKey) => {
      if (!dateKey) return '-';
      const date = new Date(`${dateKey}T12:00:00`);
      if (Number.isNaN(date.getTime())) return dateKey;
      return date.toLocaleDateString('it-IT', {
        weekday: 'short',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    };

    const normalizeServiceId = (value) => String(value || SERVICE_ID).trim().toLowerCase().replace(/[^a-z0-9_-]/g, '');

    const normalizeEmail = (value) => String(value || '').trim().toLowerCase();

    const formatCommercialDate = (timeMs) => {
      if (!timeMs) return '';
      const date = new Date(timeMs);
      if (Number.isNaN(date.getTime())) return '';
      return date.toLocaleDateString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    };

    const loadCommercialStatusBatch = async (items, { runQuery, isCurrent }) => {
      const result = await runQuery(async () => {
        const visibleItems = items.filter((item) => isCurrent(item));
        if (!visibleItems.length) return { statuses: {} };
        const user = auth.currentUser;
        if (!user) throw Object.assign(new Error('Accesso richiesto.'), { code: 'unauthenticated' });
        const token = await user.getIdToken();
        const response = await fetch(getPublicBookingCommercialStatusAdminFunctionUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ bookingIds: visibleItems.map((item) => item.id) }),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || payload.ok !== true) {
          const code = response.status === 401 ? 'unauthenticated'
            : response.status === 403 ? 'permission-denied'
              : response.status >= 500 ? 'unavailable' : 'commercial-status-failed';
          throw Object.assign(new Error('Controllo situazione commerciale non riuscito.'), { code });
        }
        return payload;
      });
      const statuses = new Map();
      if (!isCurrent()) return statuses;
      for (const item of items) {
        if (!isCurrent(item)) continue;
        const info = result.statuses?.[item.id];
        if (!info || !['active_program', 'purchased', 'lead', 'unknown'].includes(info.status)) {
          throw Object.assign(new Error('Risposta situazione commerciale incompleta.'), { code: 'commercial-status-failed' });
        }
        statuses.set(commercialCustomerKey(item), info);
      }
      return statuses;
    };

    const commercialStatusLoader = createPublicBookingCommercialLoader({
      loadBatch: loadCommercialStatusBatch,
      timeoutMs: 40000,
      onUpdate: (statuses) => { commercialStatusByBookingId.value = statuses; },
      onError: (error) => {
        // Log only the technical reason, without customer or order details.
        console.error('Controllo situazione commerciale non riuscito:', String(error?.code || 'commercial-status-failed'));
      },
    });

    const commercialVisibleBookings = computed(() => bookingView.value === 'timeline'
      ? timelineVisibleBookings.value : filteredBookings.value);

    watch(commercialVisibleBookings, (items) => {
      const force = commercialStatusRefreshRequested;
      commercialStatusRefreshRequested = false;
      commercialStatusLoader.select(items, { force });
    }, { immediate: true });

    const commercialStatusInfo = (booking) => (
      commercialStatusByBookingId.value[booking?.id] || { status: 'loading' }
    );

    const purchaseInfo = (booking) => bookingPurchaseOutcome(booking, commercialStatusInfo(booking));

    const commercialStatusIcon = (booking) => {
      const status = commercialStatusInfo(booking).status;
      if (status === 'active_program') return 'fitness_center';
      if (status === 'purchased') return 'shopping_bag';
      if (status === 'lead') return 'payments';
      if (status === 'error') return 'sync_problem';
      if (status === 'unknown') return 'help_outline';
      return 'hourglass_top';
    };

    const commercialStatusClass = (booking) => {
      const status = commercialStatusInfo(booking).status;
      if (status === 'active_program') return 'is-active-program';
      if (status === 'purchased') return 'is-purchased';
      if (status === 'lead') return 'is-lead';
      if (status === 'error') return 'is-error';
      if (status === 'unknown') return 'is-unknown';
      return 'is-loading';
    };

    const commercialStatusTitle = (booking) => {
      const info = commercialStatusInfo(booking);
      if (info.status === 'active_program') return `Programma in corso: ${info.title || 'programma attivo'}. Call di allineamento.`;
      if (info.status === 'purchased') {
        const dateLabel = formatCommercialDate(info.orderTime);
        return dateLabel ? `Acquisto registrato il ${dateLabel}.` : 'Acquisto registrato.';
      }
      if (info.status === 'lead') return 'Nessun acquisto o programma in corso trovato. Da verificare pagamento o follow-up.';
      if (info.status === 'error') {
        if (info.errorCode === 'permission-denied') return 'Accesso ad acquisti o programmi non autorizzato. Verifica i permessi dell’account.';
        if (info.errorCode === 'commercial-status-timeout' || info.errorCode === 'deadline-exceeded') return 'La verifica di acquisti e programmi sta impiegando troppo tempo. Riprova con “Aggiorna lista”.';
        if (info.errorCode === 'unavailable') return 'Connessione al servizio acquisti e programmi non disponibile. Riprova con “Aggiorna lista”.';
        return 'Non riesco a verificare acquisti e programmi. Riprova con “Aggiorna lista”.';
      }
      if (info.status === 'unknown') return 'Situazione non verificabile: mancano profilo utente o email.';
      return 'Controllo situazione commerciale in corso.';
    };

    const situationInfo = (booking) => ({
      icon: commercialStatusIcon(booking),
      className: commercialStatusClass(booking),
      title: commercialStatusTitle(booking),
      label: ({
        active_program: 'Programma in corso',
        purchased: 'Acquisto registrato',
        lead: 'Nessun acquisto rilevato',
        unknown: 'Non verificabile',
        error: 'Verifica non riuscita',
      })[commercialStatusInfo(booking).status] || 'Verifica in corso',
    });

    // L'accesso alla pagina è già limitato dal router ai ruoli Amministratore e Studio.
    // Le azioni sulle prenotazioni restano disponibili anche a chi non vede il calendario.
    const canManagePublicCalendar = computed(() => true);

    const ensurePublicCalendarAccess = () => {
      if (canManagePublicCalendar.value) return true;
      scheduleError.value = 'Accesso riservato alla gestione del calendario pubblico.';
      return false;
    };

    const clearBookingBlockFeedback = () => {
      blockError.value = '';
      blockConflicts.value = [];
    };
    const openBookingBlock = () => {
      if (!ensurePublicCalendarAccess() || blockSaving.value) return;
      clearBookingBlockFeedback();
      bookingMessage.value = '';
      bookingError.value = '';
      blockDialogOpen.value = true;
    };
    const closeBookingBlock = () => {
      if (!blockSaving.value) blockDialogOpen.value = false;
    };

    const requestBookingBlock = async (endpoint, body) => {
      const user = auth.currentUser;
      if (!user) throw new Error('Accesso richiesto. Accedi nuovamente.');
      const controller = new AbortController();
      let timer;
      const timeout = new Promise((resolve, reject) => {
        timer = setTimeout(() => {
          controller.abort();
          reject(new Error('Conferma non ricevuta. Aggiorna la lista per verificare il blocco prima di riprovare.'));
        }, 25000);
      });
      try {
        const result = await Promise.race([(async () => {
          const token = await user.getIdToken();
          if (disposed || controller.signal.aborted || auth.currentUser?.uid !== user.uid) {
            throw new Error('Sessione cambiata. Ricarica la pagina.');
          }
          const response = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ serviceId: SERVICE_ID, ...body }),
            signal: controller.signal,
          });
          const payload = await response.json().catch(() => ({}));
          if (!response.ok || payload.ok !== true) {
            throw Object.assign(new Error(payload.error || 'Non riesco a salvare il blocco. Riprova.'), {
              code: payload.code, conflicts: payload.conflicts,
            });
          }
          return payload;
        })(), timeout]);
        if (disposed || auth.currentUser?.uid !== user.uid) throw new Error('Sessione cambiata. Ricarica la pagina.');
        return result;
      } finally {
        clearTimeout(timer);
      }
    };

    const createBookingBlock = async (form) => {
      if (blockSaving.value || !ensurePublicCalendarAccess()) return;
      const userId = auth.currentUser?.uid;
      blockSaving.value = true;
      blockError.value = '';
      try {
        const payload = await requestBookingBlock(getCreatePublicBookingBlockAdminFunctionUrl(), form);
        if (!payload.block?.id || !publicBookingBlocks({ bookingBlocks: { [payload.block.id]: payload.block } }).length) {
          throw new Error('Risposta non valida. Aggiorna la lista per verificare il blocco.');
        }
        serviceConfig.value = { ...serviceConfig.value, bookingBlocks: {
          ...serviceConfig.value?.bookingBlocks, [payload.block.id]: payload.block,
        } };
        blockDialogOpen.value = false;
        clearBookingBlockFeedback();
        selectedDate.value = payload.block.dateKey;
        bookingMessage.value = 'Blocco creato: la fascia non è più prenotabile, né manualmente né online.';
        void refreshDashboard({ force: true });
      } catch (error) {
        if (disposed || auth.currentUser?.uid !== userId) return;
        blockError.value = error.message;
        if (error.code === 'booking-block-conflict' && Array.isArray(error.conflicts)) {
          blockConflicts.value = error.conflicts;
        }
      } finally {
        if (!disposed && auth.currentUser?.uid === userId) blockSaving.value = false;
      }
    };

    const removeBookingBlock = async (block) => {
      if (blockSaving.value || !ensurePublicCalendarAccess()) return;
      if (!window.confirm(`Rimuovere il blocco del ${formatDateLabel(block.dateKey)} dalle ${block.start} alle ${block.end}? Gli orari torneranno prenotabili secondo il calendario.`)) return;
      const userId = auth.currentUser?.uid;
      blockSaving.value = true;
      bookingError.value = '';
      bookingMessage.value = '';
      try {
        const payload = await requestBookingBlock(getRemovePublicBookingBlockAdminFunctionUrl(), { blockId: block.id });
        if (payload.blockId !== block.id) throw new Error('Risposta non valida. Aggiorna la lista per verificare il blocco.');
        const blocks = { ...serviceConfig.value?.bookingBlocks };
        delete blocks[block.id];
        serviceConfig.value = { ...serviceConfig.value, bookingBlocks: blocks };
        bookingMessage.value = 'Blocco rimosso. Gli orari sono nuovamente disponibili secondo il calendario.';
        void refreshDashboard({ force: true });
      } catch (error) {
        if (!disposed && auth.currentUser?.uid === userId) bookingError.value = error.message;
      } finally {
        if (!disposed && auth.currentUser?.uid === userId) blockSaving.value = false;
      }
    };

    watch(() => currentAuthUser.value?.uid, () => {
      blockDialogOpen.value = false;
      blockSaving.value = false;
      clearBookingBlockFeedback();
    });

    const firstNonEmpty = (...values) => values
      .map((value) => String(value || '').trim())
      .find(Boolean) || '';

    const bookingMissingPhone = (booking) => !String(booking?.phone || '').trim();

    const openUserProfile = (userId) => {
      if (!userId) return;
      const resolved = router.resolve({
        name: 'UserDetails',
        params: { userId: String(userId) },
      });
      window.open(resolved.href, '_blank', 'noopener');
    };

    const bookingOriginSource = (booking) => String(booking?.source || '').trim().toLowerCase();

    const isManualBookingSource = (source) => [
      'admin',
      'admin_booking',
      'manual',
      'manuale',
      'prenotazioni-pubbliche',
    ].includes(source);

    const isSiteBookingSource = (source) => [
      '',
      'fuscofit',
      'public',
      'public_booking',
      'site',
      'sito',
      'website',
    ].includes(source);

    const bookingIsOutsideCalendar = (booking) => {
      if (serviceConfig.value == null) return false;
      const dateKey = String(booking?.dateKey || '').trim();
      return Boolean(dateKey) && !configuredDateKeySet.value.has(dateKey);
    };

    const bookingOriginLabel = (booking) => {
      const source = bookingOriginSource(booking);
      if (isManualBookingSource(source)) {
        return bookingIsOutsideCalendar(booking) ? 'Manuale · fuori calendario' : 'Manuale';
      }
      if (isSiteBookingSource(source)) return 'Sito';
      return source;
    };

    const bookingOriginClass = (booking) => {
      const source = bookingOriginSource(booking);
      if (isManualBookingSource(source)) return 'is-manual';
      if (isSiteBookingSource(source)) return 'is-site';
      return 'is-unknown';
    };

    const bookingMeetingMode = (booking) => booking?.meetingMode === 'in_person' ? 'in_person' : 'online';

    const bookingMeetingModeLabel = (booking) => bookingMeetingMode(booking) === 'in_person' ? 'In presenza' : 'Online';

    const canEditBookingMeetingMode = (booking) => isManualBookingSource(bookingOriginSource(booking));

    const normalizeUserLookupResult = (docSnap) => {
      const data = docSnap.data() || {};
      return {
        uid: firstNonEmpty(data.uid, docSnap.id),
        email: firstNonEmpty(data.email, data.emailLower),
        firstName: firstNonEmpty(data.name, data.firstName, data.first_name, data.nome),
        lastName: firstNonEmpty(data.surname, data.lastName, data.last_name, data.cognome),
        phone: firstNonEmpty(data.phone, data.mobile, data.telefono, data.phoneNumber),
      };
    };

    const sanitizeEmailList = (value = '') => String(value || '')
      .split(',')
      .map((email) => normalizeEmail(email))
      .filter(Boolean);

    const toDatetimeLocalValue = (date) => {
      if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    const nextBusinessFollowUpDate = (booking) => {
      const now = new Date();
      const base = booking?.startAtDate instanceof Date && booking.startAtDate > now
        ? booking.startAtDate
        : now;
      const due = new Date(base);
      due.setDate(due.getDate() + 1);
      due.setHours(10, 0, 0, 0);
      while (due.getDay() === 0 || due.getDay() === 6) {
        due.setDate(due.getDate() + 1);
      }
      return due;
    };

    const buildFollowUpNote = (booking) => {
      const name = booking?.fullName || [booking?.firstName, booking?.lastName].filter(Boolean).join(' ').trim() || 'Cliente';
      const dateLabel = booking?.dateKey ? formatDateLabel(booking.dateKey) : '-';
      const appointmentLabel = bookingMeetingMode(booking) === 'in_person' ? 'incontro in presenza' : 'video call';
      const rows = [
        `Ricontatto post ${appointmentLabel} del ${dateLabel} alle ${booking?.timeKey || '-'}.`,
        `Cliente: ${name}`,
        `Email: ${booking?.email || '-'}`,
      ];
      if (booking?.phone) rows.push(`Telefono: ${booking.phone}`);
      if (booking?.note) rows.push(`Note prenotazione: ${booking.note}`);
      rows.push('Verificare se ha acquistato o se serve supporto alla scelta.');
      return rows.join('\n');
    };

    const findExistingFollowUpReminder = async (bookingId) => {
      if (!bookingId) return null;
      const db = getFirestore();
      const snap = await getDocs(query(
        collection(db, REMINDERS_COLLECTION),
        where('publicBookingId', '==', bookingId),
        limit(1)
      ));
      if (snap.empty) return null;
      const docSnap = snap.docs[0];
      return { id: docSnap.id, ...docSnap.data() };
    };

    const findFollowUpReminderById = async (reminderId) => {
      if (!reminderId) return null;
      const db = getFirestore();
      const snap = await getDoc(doc(db, REMINDERS_COLLECTION, reminderId));
      return snap.exists() ? { id: snap.id, ...snap.data() } : null;
    };

    const clearBookingFollowUpReminderRef = async (booking) => {
      if (!booking?.id) return;
      const staleReminderId = booking.followUpReminderId || '';
      booking.followUpReminderId = '';
      try {
        const db = getFirestore();
        const bookingRef = doc(db, BOOKINGS_COLLECTION, booking.id);
        const bookingSnap = await getDoc(bookingRef);
        const currentReminderId = bookingSnap.exists() ? (bookingSnap.data()?.followUpReminderId || '') : '';
        if (!currentReminderId || currentReminderId === staleReminderId) {
          await updateDoc(bookingRef, {
            followUpReminderId: deleteField(),
            followUpReminderAt: deleteField(),
          });
        }
      } catch (error) {
        console.warn('Non riesco a pulire il riferimento promemoria sulla prenotazione:', error);
      }
    };

    const reminderBookingLabel = computed(() => {
      const booking = reminderBooking.value;
      if (!booking) return '';
      const name = booking.fullName || booking.email || booking.id;
      return `${name} - ${formatDateLabel(booking.dateKey)} ${booking.timeKey || ''}`;
    });

    const resetFollowUpReminder = () => {
      reminderModalOpen.value = false;
      reminderBooking.value = null;
      reminderForm.value = defaultFollowUpReminderForm();
      reminderError.value = '';
      reminderMessage.value = '';
    };

    const openFollowUpReminder = async (booking) => {
      if (!ensurePublicCalendarAccess()) return;
      bookingError.value = '';
      bookingMessage.value = '';
      reminderError.value = '';
      reminderMessage.value = '';

      if (!booking?.id) {
        bookingError.value = 'Prenotazione non valida.';
        return;
      }
      if (!booking.userId) {
        bookingError.value = 'Questa prenotazione non ha un profilo utente associato.';
        return;
      }

      reminderSaving.value = true;
      try {
        let existingReminder = booking.followUpReminderId
          ? await findFollowUpReminderById(booking.followUpReminderId)
          : null;
        if (booking.followUpReminderId && !existingReminder) {
          await clearBookingFollowUpReminderRef(booking);
        }
        if (!existingReminder) {
          existingReminder = await findExistingFollowUpReminder(booking.id);
        }
        if (existingReminder?.id) {
          booking.followUpReminderId = existingReminder.id;
          bookingMessage.value = 'Promemoria follow-up gia creato per questa prenotazione.';
          return;
        }

        reminderBooking.value = booking;
        reminderForm.value = {
          ...defaultFollowUpReminderForm(),
          data: toDatetimeLocalValue(nextBusinessFollowUpDate(booking)),
          assignedEmail: normalizeEmail(currentAuthUser.value?.email),
          note: buildFollowUpNote(booking),
        };
        reminderModalOpen.value = true;
      } catch (error) {
        console.error('Errore controllo promemoria follow-up:', error);
        bookingError.value = 'Non riesco a controllare i promemoria esistenti.';
      } finally {
        reminderSaving.value = false;
      }
    };

    const validateFollowUpReminder = () => {
      const booking = reminderBooking.value;
      const form = reminderForm.value;
      if (!booking?.id || !booking.userId) return 'Prenotazione senza profilo utente associato.';
      const dueDate = new Date(form.data);
      if (!form.data || Number.isNaN(dueDate.getTime())) return 'Seleziona una data promemoria valida.';
      const assignedEmails = sanitizeEmailList(form.assignedEmail);
      if (!assignedEmails.length) return 'Inserisci almeno una email assegnata.';
      const invalidEmail = assignedEmails.find((email) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
      if (invalidEmail) return `Email assegnata non valida: ${invalidEmail}`;
      return '';
    };

    const createFollowUpReminder = async () => {
      reminderError.value = '';
      reminderMessage.value = '';
      const validationError = validateFollowUpReminder();
      if (validationError) {
        reminderError.value = validationError;
        return;
      }

      reminderSaving.value = true;
      try {
        const booking = reminderBooking.value;
        const existingReminder = await findExistingFollowUpReminder(booking.id);
        if (existingReminder?.id) {
          booking.followUpReminderId = existingReminder.id;
          reminderError.value = 'Promemoria follow-up gia creato per questa prenotazione.';
          return;
        }

        const db = getFirestore();
        const dueAt = new Date(reminderForm.value.data);
        const assignedEmail = sanitizeEmailList(reminderForm.value.assignedEmail).join(', ');
        const docRef = await addDoc(collection(db, REMINDERS_COLLECTION), {
          uid: booking.userId,
          userEmail: booking.email || '',
          data: Timestamp.fromDate(dueAt),
          tipo: reminderForm.value.tipo || 'whatsapp',
          urgenza: reminderForm.value.urgenza || 'media',
          note: reminderForm.value.note || '',
          assignedEmail,
          tipoPromemoria: 'pubblico',
          fatto: 'da fare',
          visibilita: true,
          source: 'public_booking_followup',
          publicBookingId: booking.id,
          bookingDateKey: booking.dateKey || '',
          bookingTimeKey: booking.timeKey || '',
          bookingServiceId: booking.serviceId || SERVICE_ID,
          bookingMeetingMode: bookingMeetingMode(booking),
          bookingCustomerEmail: booking.email || '',
          bookingCustomerPhone: booking.phone || '',
          stato3: 'da ricontattare',
          acquistato: 'In attesa',
          createdAt: serverTimestamp(),
          createdBy: currentAuthUser.value?.uid || null,
          createdByEmail: normalizeEmail(currentAuthUser.value?.email),
        });

        try {
          await updateDoc(doc(db, BOOKINGS_COLLECTION, booking.id), {
            followUpReminderId: docRef.id,
            followUpReminderAt: serverTimestamp(),
          });
          booking.followUpReminderId = docRef.id;
        } catch (error) {
          console.warn('Promemoria creato, ma non riesco ad aggiornare la prenotazione:', error);
        }

        resetFollowUpReminder();
        bookingMessage.value = 'Promemoria follow-up creato. Lo trovi in dashboard nei tuoi promemoria.';
      } catch (error) {
        console.error('Errore creazione promemoria follow-up:', error);
        reminderError.value = error.message || 'Non riesco a creare il promemoria.';
      } finally {
        reminderSaving.value = false;
      }
    };

    const resetDateForm = () => {
      editingDateKey.value = '';
      dateForm.value = defaultDateForm();
      scheduleError.value = '';
      scheduleMessage.value = '';
    };

    const validateDateForm = () => {
      const dateKey = String(dateForm.value.dateKey || '').trim();
      if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) return 'Inserisci una data valida.';
      const start = minutesFromTime(dateForm.value.start);
      const end = minutesFromTime(dateForm.value.end);
      if (start == null || end == null || end <= start) return 'Inserisci un intervallo orario valido.';
      const slotMinutes = Number(dateForm.value.slotMinutes || 0);
      if (!Number.isInteger(slotMinutes) || slotMinutes < 5) return 'La durata slot deve essere un numero intero di almeno 5 minuti.';
      if ((end - start) < slotMinutes) return 'La durata slot e piu lunga dell’intervallo orario.';
      return '';
    };

    const saveBookingDate = async () => {
      if (!ensurePublicCalendarAccess()) return;
      scheduleError.value = '';
      scheduleMessage.value = '';
      const validationError = validateDateForm();
      if (validationError) {
        scheduleError.value = validationError;
        return;
      }

      const dateKey = String(dateForm.value.dateKey || '').trim();
      const alreadyExists = configuredDates.value.some((date) => date.dateKey === dateKey);
      if (!editingDateKey.value && alreadyExists) {
        scheduleError.value = 'Questa data esiste gia. Usa il pulsante modifica sulla riga corrispondente.';
        return;
      }

      scheduleSaving.value = true;
      try {
        const user = auth.currentUser;
        if (!user) throw new Error('Accesso richiesto.');
        const token = await user.getIdToken();
        const response = await fetch(getUpdatePublicBookingDateAdminFunctionUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            serviceId: SERVICE_ID,
            dateKey,
            start: dateForm.value.start,
            end: dateForm.value.end,
            slotMinutes: Number(dateForm.value.slotMinutes),
            visibility: dateForm.value.visibility === 'hidden' ? 'hidden' : 'public',
          }),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload?.error || 'Non riesco a salvare la data prenotabile.');

        let nextMessage = editingDateKey.value
          ? 'Data aggiornata correttamente.'
          : 'Data prenotabile creata correttamente.';
        const updatedBookings = Number(payload.updatedBookings || 0);
        if (updatedBookings > 0) {
          nextMessage += ` Durata aggiornata per ${updatedBookings} ${updatedBookings === 1 ? 'appuntamento' : 'appuntamenti'}.`;
        }
        resetDateForm();
        scheduleMessage.value = nextMessage;
        await refreshDashboard({ force: true });
        if (manualBookingOpen.value) await fetchManualBookingAvailability();
      } catch (error) {
        console.error('Errore salvataggio data prenotabile:', error);
        scheduleError.value = error.message || 'Non riesco a salvare la data prenotabile.';
      } finally {
        scheduleSaving.value = false;
      }
    };

    const editBookingDate = (date) => {
      if (!ensurePublicCalendarAccess()) return;
      scheduleEditorOpen.value = true;
      editingDateKey.value = date.dateKey;
      dateForm.value = {
        dateKey: date.dateKey,
        start: date.start,
        end: date.end,
        slotMinutes: date.slotMinutes,
        visibility: date.visibility,
      };
      scheduleError.value = '';
      scheduleMessage.value = '';
    };

    const saveDateOverride = async (dateKey, payload) => {
      const db = getFirestore();
      await setDoc(serviceRef(db), {
        dateOverrides: {
          [dateKey]: payload,
        },
        updatedAt: serverTimestamp(),
      }, { merge: true });
    };

    const toggleDateVisibility = async (date) => {
      if (!ensurePublicCalendarAccess()) return;
      scheduleSaving.value = true;
      scheduleError.value = '';
      scheduleMessage.value = '';
      try {
        const nextVisibility = date.visibility === 'public' ? 'hidden' : 'public';
        await saveDateOverride(date.dateKey, {
          visibility: nextVisibility,
          ranges: date.ranges,
        });
        scheduleMessage.value = nextVisibility === 'public' ? 'Data resa visibile.' : 'Data nascosta.';
        await refreshDashboard({ force: true });
      } catch (error) {
        console.error('Errore cambio visibilita data prenotabile:', error);
        scheduleError.value = 'Non riesco ad aggiornare la visibilita della data.';
      } finally {
        scheduleSaving.value = false;
      }
    };

    const deleteBookingDate = async (date) => {
      if (!ensurePublicCalendarAccess()) return;
      if (serviceLoading.value || errorMessage.value || date.bookingCount == null) {
        scheduleError.value = 'Carica le prenotazioni della data prima di eliminarla.';
        return;
      }
      if (date.bookingCount > 0) {
        scheduleError.value = 'Non elimino date con prenotazioni: puoi nasconderla.';
        return;
      }
      const confirmed = window.confirm(`Eliminare la data ${date.dateKey}?`);
      if (!confirmed) return;

      scheduleSaving.value = true;
      scheduleError.value = '';
      scheduleMessage.value = '';
      try {
        const db = getFirestore();
        await updateDoc(serviceRef(db), {
          [`dateOverrides.${date.dateKey}`]: deleteField(),
          updatedAt: serverTimestamp(),
        });
        if (editingDateKey.value === date.dateKey) resetDateForm();
        scheduleMessage.value = 'Data eliminata.';
        await refreshDashboard({ force: true });
      } catch (error) {
        console.error('Errore eliminazione data prenotabile:', error);
        scheduleError.value = 'Non riesco a eliminare la data.';
      } finally {
        scheduleSaving.value = false;
      }
    };

    const resetBookingEdit = () => {
      editingBookingId.value = '';
      editingBooking.value = null;
      bookingForm.value = defaultBookingForm();
      bookingError.value = '';
      bookingMessage.value = '';
    };

    const fetchManualBookingAvailability = async () => {
      const requestId = ++manualBookingAvailabilityRequestId;
      manualBookingAvailabilityLoading.value = true;
      manualBookingAvailabilityError.value = '';

      try {
        const user = auth.currentUser;
        if (!user) throw new Error('Accesso richiesto.');
        const token = await user.getIdToken();
        const endpoint = getPublicBookingAvailabilityAdminFunctionUrl();
        const separator = endpoint.includes('?') ? '&' : '?';
        const resp = await fetch(`${endpoint}${separator}service=${encodeURIComponent(SERVICE_ID)}`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const payload = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(payload?.error || 'Non riesco a caricare le disponibilita.');
        if (requestId !== manualBookingAvailabilityRequestId) return;
        manualBookingAvailability.value = Array.isArray(payload?.dates) ? payload.dates : [];
      } catch (error) {
        if (requestId !== manualBookingAvailabilityRequestId) return;
        console.error('Errore disponibilita calendario admin:', error);
        manualBookingAvailability.value = [];
        manualBookingAvailabilityError.value = error.message || 'Non riesco a caricare le disponibilita.';
      } finally {
        if (requestId === manualBookingAvailabilityRequestId) {
          manualBookingAvailabilityLoading.value = false;
        }
      }
    };

    const setManualBookingMode = async (mode) => {
      const nextMode = mode === 'free' ? 'free' : 'calendar';
      if (manualBookingMode.value === nextMode) return;
      manualBookingMode.value = nextMode;
      manualBookingForm.value = {
        ...manualBookingForm.value,
        dateKey: '',
        timeKey: '',
      };
      bookingError.value = '';
      bookingMessage.value = '';
      if (nextMode === 'calendar') await fetchManualBookingAvailability();
    };

    const handleManualBookingFreeDateChange = () => {
      manualBookingForm.value.timeKey = '';
      bookingError.value = '';
    };

    const openManualBooking = async () => {
      refreshTodayDateKey();
      manualBookingOpen.value = true;
      manualBookingMode.value = 'calendar';
      manualBookingForm.value = defaultManualBookingForm();
      manualBookingAvailability.value = [];
      manualBookingAvailabilityError.value = '';
      bookingError.value = '';
      bookingMessage.value = '';
      manualUserLookupMessage.value = '';
      manualUserLookupError.value = '';
      manualUserLookupCanCreate.value = false;
      await fetchManualBookingAvailability();
      if (
        manualBookingOpen.value
        && !manualBookingAvailabilityError.value
        && !manualCalendarDates.value.length
      ) {
        manualBookingMode.value = 'free';
      }
    };

    const createDeepLinkUserId = () => {
      if (String(route.query?.create || '') !== '1') return '';
      const value = Array.isArray(route.query?.uid) ? route.query.uid[0] : route.query?.uid;
      const uid = String(value || '').trim();
      return /^[A-Za-z0-9_-]{6,128}$/.test(uid) ? uid : '';
    };

    const openManualBookingFromDeepLink = async () => {
      const uid = createDeepLinkUserId();
      if (!uid || manualBookingDeepLinkHandled || !currentAuthUser.value) return;
      manualBookingDeepLinkHandled = true;
      const opening = openManualBooking();
      manualUserLookupLoading.value = true;
      manualUserLookupMessage.value = '';
      manualUserLookupError.value = '';
      try {
        const db = getFirestore();
        const userSnapshot = await getDoc(doc(db, 'users', uid));
        if (!userSnapshot.exists()) throw new Error('Profilo utente non trovato.');
        const user = normalizeUserLookupResult(userSnapshot);
        if (!user.email) throw new Error('Il profilo non contiene un indirizzo email.');
        manualBookingForm.value = {
          ...manualBookingForm.value,
          userId: uid,
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          phone: user.phone,
        };
        manualUserLookupMessage.value = 'Utente recuperato dal monitoraggio.';
      } catch (error) {
        console.error('Errore precompilazione prenotazione dal monitoraggio:', error);
        manualUserLookupError.value = `${error.message || 'Utente non disponibile.'} Puoi compilare i dati manualmente.`;
      } finally {
        manualUserLookupLoading.value = false;
      }
      await opening;
    };

    const resetManualBooking = () => {
      manualBookingAvailabilityRequestId += 1;
      manualBookingOpen.value = false;
      manualBookingMode.value = 'calendar';
      manualBookingForm.value = defaultManualBookingForm();
      manualBookingAvailability.value = [];
      manualBookingAvailabilityLoading.value = false;
      manualBookingAvailabilityError.value = '';
      bookingError.value = '';
      bookingMessage.value = '';
      manualUserLookupMessage.value = '';
      manualUserLookupError.value = '';
      manualUserLookupCanCreate.value = false;
    };

    const clearManualUserLookup = () => {
      manualUserLookupMessage.value = '';
      manualUserLookupError.value = '';
      manualUserLookupCanCreate.value = false;
      manualBookingForm.value.userId = '';
    };

    const validateManualUserData = () => {
      const form = manualBookingForm.value;
      const email = normalizeEmail(form.email);
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Inserisci una email valida.';
      if (!String(form.firstName || '').trim() || !String(form.lastName || '').trim()) {
        return 'Nome e cognome sono obbligatori per creare l’utente.';
      }
      return '';
    };

    const createManualUserFromForm = async () => {
      manualUserLookupMessage.value = '';
      manualUserLookupError.value = '';
      const validationError = validateManualUserData();
      if (validationError) {
        manualUserLookupError.value = validationError;
        throw new Error(validationError);
      }

      manualUserCreateLoading.value = true;
      try {
        const form = manualBookingForm.value;
        const firebaseFunctions = getFunctions();
        const createUserFn = httpsCallable(firebaseFunctions, 'createUser');
        const res = await createUserFn({
          email: normalizeEmail(form.email),
          name: String(form.firstName || '').trim(),
          surname: String(form.lastName || '').trim(),
          phone: String(form.phone || '').trim(),
        });
        const uid = res?.data?.uid || res?.data || '';
        if (!uid) throw new Error('Utente creato senza UID.');

        manualBookingForm.value = {
          ...manualBookingForm.value,
          email: normalizeEmail(form.email),
          userId: uid,
        };
        manualUserLookupCanCreate.value = false;
        manualUserLookupMessage.value = 'Utente creato. Dati compilati.';
        bookingError.value = '';
        return uid;
      } catch (error) {
        const message = String(error?.message || '').toLowerCase();
        if (message.includes('already') || message.includes('già registrata') || message.includes('already-exists')) {
          await findManualBookingUserByEmail();
          if (manualBookingForm.value.userId) return manualBookingForm.value.userId;
        }
        console.error('Errore creazione utente prenotazione pubblica:', error);
        manualUserLookupError.value = error.message || 'Non riesco a creare l’utente.';
        throw error;
      } finally {
        manualUserCreateLoading.value = false;
      }
    };

    const createManualUserFromButton = async () => {
      try {
        await createManualUserFromForm();
      } catch {
        // Il messaggio visibile viene impostato da createManualUserFromForm.
      }
    };

    const findManualBookingUserByEmail = async () => {
      const rawEmail = String(manualBookingForm.value.email || '').trim();
      const email = normalizeEmail(rawEmail);
      manualUserLookupMessage.value = '';
      manualUserLookupError.value = '';
      manualUserLookupCanCreate.value = false;

      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        manualUserLookupError.value = 'Inserisci una email valida.';
        return;
      }

      manualUserLookupLoading.value = true;
      try {
        const db = getFirestore();
        const usersRef = collection(db, 'users');
        const attempts = [
          { field: 'emailLower', value: email },
          { field: 'email', value: email },
        ];
        if (rawEmail && rawEmail !== email) attempts.push({ field: 'email', value: rawEmail });

        let foundUser = null;
        const seenAttempts = new Set();
        for (const attempt of attempts) {
          const attemptKey = `${attempt.field}:${attempt.value}`;
          if (!attempt.value || seenAttempts.has(attemptKey)) continue;
          seenAttempts.add(attemptKey);

          const snap = await getDocs(query(
            usersRef,
            where(attempt.field, '==', attempt.value),
            limit(1)
          ));
          if (!snap.empty) {
            foundUser = normalizeUserLookupResult(snap.docs[0]);
            break;
          }
        }

        if (!foundUser) {
          manualBookingForm.value.userId = '';
          manualUserLookupError.value = 'Nessun utente trovato con questa email.';
          manualUserLookupCanCreate.value = true;
          return;
        }

        manualBookingForm.value = {
          ...manualBookingForm.value,
          email: foundUser.email || email,
          firstName: foundUser.firstName || manualBookingForm.value.firstName,
          lastName: foundUser.lastName || manualBookingForm.value.lastName,
          phone: foundUser.phone || manualBookingForm.value.phone,
          userId: foundUser.uid,
        };
        manualUserLookupCanCreate.value = false;
        manualUserLookupMessage.value = 'Utente trovato. Dati compilati.';
      } catch (error) {
        console.error('Errore ricerca utente prenotazione pubblica:', error);
        manualUserLookupError.value = 'Non riesco a cercare l’utente.';
      } finally {
        manualUserLookupLoading.value = false;
      }
    };

    const editBooking = (booking) => {
      if (!ensurePublicCalendarAccess()) return;
      editingBookingId.value = booking.id;
      editingBooking.value = booking;
      bookingForm.value = {
        dateKey: booking.dateKey || '',
        timeKey: booking.timeKey || '',
        note: booking.note || '',
        meetingMode: bookingMeetingMode(booking),
      };
      bookingError.value = '';
      bookingMessage.value = '';
      // Refresh while the form is filled, reusing the same service as the save.
      void refreshDashboard();
    };

    const saveBookingNote = async (booking, note) => {
      if (!booking?.id || !canManagePublicCalendar.value || disposed) {
        throw new Error('Non puoi modificare le note di questa prenotazione.');
      }
      if (bookingSaving.value || isLoading.value) {
        throw new Error('Attendi il completamento dell’operazione in corso e riprova.');
      }
      const user = auth.currentUser;
      if (!user) throw new Error('Accesso richiesto. Accedi nuovamente.');
      const nextNote = String(note ?? '').trim();
      if (nextNote.length > 2000) throw new Error('Le note possono contenere al massimo 2000 caratteri.');
      const current = bookings.value.find(item => item.id === booking.id);
      if (!current) throw new Error('Prenotazione non trovata. Aggiorna la lista.');
      if (nextNote === current.note) return nextNote;

      bookingSaving.value = true;
      const controller = new AbortController();
      let timer;
      const timeout = new Promise((resolve, reject) => {
        timer = setTimeout(() => {
          controller.abort();
          reject(Object.assign(new Error('Tempo di attesa superato.'), { name: 'AbortError' }));
        }, 25000);
      });
      try {
        const payload = await Promise.race([(async () => {
          const token = await user.getIdToken();
          if (disposed || controller.signal.aborted || auth.currentUser?.uid !== user.uid) {
            throw new Error('Sessione cambiata. Accedi nuovamente prima di salvare.');
          }
          const response = await fetch(getUpdatePublicBookingAdminFunctionUrl(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ bookingId: booking.id, note: nextNote }),
            signal: controller.signal,
          });
          const result = await response.json().catch(() => ({}));
          if (!response.ok || result.ok !== true || result.bookingId !== booking.id || typeof result.note !== 'string') {
            throw new Error(result.error || 'Non riesco a salvare le note. Riprova.');
          }
          return result;
        })(), timeout]);
        if (disposed || auth.currentUser?.uid !== user.uid) throw new Error('Sessione cambiata. Ricarica la pagina.');
        // Both views read this collection; a note-only response must not change the schedule.
        bookings.value = bookings.value.map(item => item.id === booking.id ? { ...item, note: payload.note } : item);
        // Replace any read started before the confirmed save; normally no reload is needed.
        if (isLoading.value) void refreshDashboard({ force: true });
        return payload.note;
      } catch (error) {
        if (error.name === 'AbortError') {
          throw new Error('Conferma del salvataggio non ricevuta. Aggiorna la lista per verificare le note prima di riprovare.');
        }
        throw error;
      } finally {
        clearTimeout(timer);
        if (!disposed && auth.currentUser?.uid === user.uid) bookingSaving.value = false;
      }
    };

    const validateBookingEdit = () => {
      if (!editingBooking.value?.id) return 'Seleziona una prenotazione da modificare.';
      if (!/^\d{4}-\d{2}-\d{2}$/.test(bookingForm.value.dateKey)) return 'Seleziona una data valida.';
      const parsedDate = new Date(`${bookingForm.value.dateKey}T12:00:00`);
      if (
        Number.isNaN(parsedDate.getTime())
        || formatDateKeyFromDate(parsedDate) !== bookingForm.value.dateKey
      ) return 'Seleziona una data valida.';
      if (
        !/^\d{2}:\d{2}$/.test(bookingForm.value.timeKey)
        || minutesFromTime(bookingForm.value.timeKey) == null
      ) return 'Seleziona un orario valido.';
      return '';
    };

    const saveBookingEdit = async () => {
      if (bookingSaving.value) return;
      if (!ensurePublicCalendarAccess()) return;
      bookingError.value = '';
      bookingMessage.value = '';
      const original = editingBooking.value;
      if (!original?.id) {
        bookingError.value = 'Seleziona una prenotazione da modificare.';
        return;
      }
      const nextDateKey = bookingForm.value.dateKey;
      const nextTimeKey = bookingForm.value.timeKey;
      const nextNote = String(bookingForm.value.note || '').trim();
      const nextMeetingMode = canEditBookingMeetingMode(original)
        ? (bookingForm.value.meetingMode ?? bookingMeetingMode(original))
        : 'online';
      if (!['online', 'in_person'].includes(nextMeetingMode)) {
        bookingError.value = 'Seleziona una modalità appuntamento valida.';
        return;
      }
      const originalNote = String(original.note || '').trim();
      const hasScheduleChange = original.dateKey !== nextDateKey || original.timeKey !== nextTimeKey;
      const hasNoteChange = originalNote !== nextNote;
      const hasMeetingModeChange = bookingMeetingMode(original) !== nextMeetingMode;
      if (hasMeetingModeChange && !inPersonBookingsEnabled.value) {
        bookingError.value = 'La modifica della modalità non è ancora disponibile. Aggiorna la lista e riprova.';
        return;
      }
      if (hasScheduleChange) {
        const validationError = validateBookingEdit();
        if (validationError) {
          bookingError.value = validationError;
          return;
        }
      }
      if (!hasScheduleChange && !hasNoteChange && !hasMeetingModeChange) {
        bookingMessage.value = 'Prenotazione gia aggiornata.';
        return;
      }

      bookingSaving.value = true;
      try {
        const user = auth.currentUser;
        if (!user) throw new Error('Accesso richiesto.');
        const token = await user.getIdToken();
        const resp = await fetch(getUpdatePublicBookingAdminFunctionUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            bookingId: original.id,
            dateKey: nextDateKey,
            timeKey: nextTimeKey,
            note: nextNote,
            meetingMode: nextMeetingMode,
          }),
        });
        const payload = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(payload?.error || 'Non riesco a modificare la prenotazione.');

        // Reflect the confirmed write immediately, even when the following read fails.
        const savedDateKey = payload.dateKey || nextDateKey;
        const savedTimeKey = payload.timeKey || nextTimeKey;
        const savedEndAt = toDate(payload.endAt);
        const savedDuration = Number(payload.durationMinutes);
        const savedStartAt = savedEndAt && savedDuration > 0
          ? new Date(savedEndAt.getTime() - savedDuration * 60000)
          : original.startAtDate;
        bookings.value = sortBookings(bookings.value.map((booking) => booking.id === original.id ? {
          ...booking,
          dateKey: savedDateKey,
          timeKey: savedTimeKey,
          note: payload.note ?? nextNote,
          meetingMode: payload.meetingMode ?? nextMeetingMode,
          startAtDate: savedStartAt,
          slotId: hasScheduleChange
            ? slotIdFor(normalizeServiceId(original.serviceId || SERVICE_ID), savedDateKey, savedTimeKey)
            : original.slotId,
          outOfSchedule: typeof payload.outOfSchedule === 'boolean' ? payload.outOfSchedule : original.outOfSchedule,
        } : booking));
        const keepSelectedDate = selectedDate.value;
        resetBookingEdit();
        bookingMessage.value = 'Prenotazione aggiornata correttamente.';
        selectedDate.value = keepSelectedDate === original.dateKey ? savedDateKey : keepSelectedDate;
        // The write is complete; the bounded refresh must not extend the saving state.
        void refreshDashboard({ force: true });
      } catch (error) {
        console.error('Errore modifica prenotazione pubblica:', error);
        bookingError.value = error.message || 'Non riesco a modificare la prenotazione.';
      } finally {
        bookingSaving.value = false;
      }
    };

    const validateManualBooking = () => {
      refreshTodayDateKey();
      const form = manualBookingForm.value;
      if (!['online', 'in_person'].includes(form.meetingMode)) return 'Seleziona una modalità appuntamento valida.';
      if (form.meetingMode === 'in_person' && !inPersonBookingsEnabled.value) {
        return 'Gli appuntamenti in presenza non sono ancora disponibili. Aggiorna la lista e riprova.';
      }
      if (!/^\d{4}-\d{2}-\d{2}$/.test(form.dateKey)) return 'Seleziona una data valida.';
      const parsedDate = new Date(`${form.dateKey}T12:00:00`);
      if (Number.isNaN(parsedDate.getTime()) || formatDateKeyFromDate(parsedDate) !== form.dateKey) {
        return 'Seleziona una data valida.';
      }
      if (form.dateKey < todayDateKey.value) return 'Non puoi creare una prenotazione in una data passata.';
      if (manualBookingClosedDate.value) return 'Questa data e chiusa. Scegli un’altra giornata.';
      if (manualBookingMode.value === 'calendar') {
        if (manualBookingAvailabilityLoading.value) return 'Attendi il caricamento delle disponibilita.';
        if (manualBookingAvailabilityError.value) return manualBookingAvailabilityError.value;
        const selectedDate = manualCalendarDates.value
          .find((date) => date.dateKey === form.dateKey);
        if (!selectedDate) return 'Seleziona una data disponibile dal calendario.';
        if (!selectedDate.slots.some((slot) => slot.timeKey === form.timeKey)) {
          return 'Questo orario non è più presente nel calendario. Selezionane un altro.';
        }
      }
      const selectedMinute = minutesFromTime(form.timeKey);
      if (!/^\d{2}:\d{2}$/.test(form.timeKey) || selectedMinute == null) {
        return 'Seleziona un orario valido.';
      }
      if (form.dateKey === todayDateKey.value && selectedMinute <= currentServiceTimeMinutes()) {
        return 'Seleziona un orario futuro.';
      }
      const configuredSlot = generateAdminSlotsForDate(form.dateKey, { includeHidden: true })
        .find((slot) => slot.timeKey === form.timeKey);
      const durationMinutes = Math.max(
        5,
        Number(configuredSlot?.slotMinutes || OUT_OF_SCHEDULE_DURATION_MINUTES)
      );
      if (selectedMinute + durationMinutes >= 24 * 60) {
        return 'La prenotazione deve terminare nello stesso giorno.';
      }
      if (isTimeBlocked(form.dateKey, form.timeKey, durationMinutes)) {
        return 'Questa fascia è bloccata. Scegli un altro orario.';
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizeEmail(form.email))) return 'Inserisci una email valida.';
      if (!String(form.firstName || '').trim() || !String(form.lastName || '').trim() || !normalizeEmail(form.email)) {
        return 'Nome, cognome ed email sono obbligatori.';
      }
      return '';
    };

    const createManualBooking = async () => {
      if (bookingSaving.value || manualUserLookupLoading.value || manualUserCreateLoading.value) return;
      bookingError.value = '';
      bookingMessage.value = '';
      const validationError = validateManualBooking();
      if (validationError) {
        bookingError.value = validationError;
        return;
      }
      if (!manualBookingForm.value.userId) {
        await findManualBookingUserByEmail();
        if (!manualBookingForm.value.userId) {
          bookingError.value = manualUserLookupCanCreate.value
            ? 'Utente non trovato. Crealo con il pulsante dedicato, poi salva la prenotazione.'
            : (manualUserLookupError.value || 'Non riesco a verificare l’utente. Riprova.');
          return;
        }
      }
      if (manualBookingIsInternal.value) {
        const form = manualBookingForm.value;
        const confirmed = window.confirm(
          `Confermi la prenotazione fuori calendario per ${formatDateLabel(form.dateKey)} alle ${form.timeKey}? `
          + 'L’eccezione non verra pubblicata; il cliente ricevera conferma.'
        );
        if (!confirmed) return;
      }

      bookingSaving.value = true;
      try {
        const user = auth.currentUser;
        if (!user) throw new Error('Accesso richiesto.');
        const token = await user.getIdToken();
        const form = manualBookingForm.value;
        const resp = await fetch(getCreatePublicBookingAdminFunctionUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            serviceId: SERVICE_ID,
            dateKey: form.dateKey,
            timeKey: form.timeKey,
            meetingMode: form.meetingMode,
            userId: form.userId || '',
            customer: {
              firstName: form.firstName,
              lastName: form.lastName,
              email: normalizeEmail(form.email),
              phone: String(form.phone || '').trim(),
              note: String(form.note || '').trim(),
            },
          }),
        });
        const payload = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          const responseError = payload?.error || 'Non riesco a creare la prenotazione.';
          if (resp.status === 409) {
            await fetchManualBookingAvailability();
            if (manualBookingMode.value === 'calendar') {
              const selectedDateStillAvailable = manualCalendarDates.value
                .some((date) => date.dateKey === form.dateKey);
              const selectedTimeStillAvailable = manualCalendarTimeOptions.value
                .some((slot) => slot.timeKey === form.timeKey);
              if (!selectedDateStillAvailable) {
                manualBookingForm.value.dateKey = '';
                manualBookingForm.value.timeKey = '';
              } else if (!selectedTimeStillAvailable) {
                manualBookingForm.value.timeKey = '';
              }
            }
          }
          throw new Error(responseError);
        }

        applyCreatedBooking(payload);
        const createdDate = payload.dateKey || form.dateKey;
        resetManualBooking();
        selectedDate.value = createdDate;
        bookingMessage.value = 'Prenotazione creata correttamente.';
        void refreshDashboard({ force: true });
      } catch (error) {
        console.error('Errore creazione prenotazione pubblica:', error);
        bookingError.value = error.message || 'Non riesco a creare la prenotazione.';
      } finally {
        bookingSaving.value = false;
      }
    };

    const deleteBooking = async (booking) => {
      if (!ensurePublicCalendarAccess()) return;
      const confirmed = window.confirm(`Cancellare la prenotazione di ${booking.fullName || booking.email || booking.id}?`);
      if (!confirmed) return;

      bookingSaving.value = true;
      bookingError.value = '';
      bookingMessage.value = '';
      try {
        const db = getFirestore();
        const serviceId = normalizeServiceId(booking.serviceId || SERVICE_ID);
        const bookingRef = doc(db, BOOKINGS_COLLECTION, booking.id);
        const slotRef = doc(db, SLOTS_COLLECTION, booking.slotId || slotIdFor(serviceId, booking.dateKey, booking.timeKey));
        const lockRef = booking.userId && booking.dateKey
          ? doc(db, LOCKS_COLLECTION, lockIdFor(serviceId, booking.userId, booking.dateKey))
          : null;

        await runTransaction(db, async (transaction) => {
          const [bookingSnap, slotSnap, lockSnap] = await Promise.all([
            transaction.get(bookingRef),
            transaction.get(slotRef),
            lockRef ? transaction.get(lockRef) : Promise.resolve(null),
          ]);

          if (!bookingSnap.exists()) throw new Error('Prenotazione non trovata.');
          const currentBooking = bookingSnap.data() || {};
          const currentServiceId = normalizeServiceId(currentBooking.serviceId || SERVICE_ID);
          const currentSlotId = currentBooking.slotId
            || slotIdFor(currentServiceId, currentBooking.dateKey, currentBooking.timeKey);
          const expectedSlotId = booking.slotId || slotIdFor(serviceId, booking.dateKey, booking.timeKey);
          if (
            currentServiceId !== serviceId
            || currentBooking.dateKey !== booking.dateKey
            || currentBooking.timeKey !== booking.timeKey
            || currentSlotId !== expectedSlotId
            || (currentBooking.userId || '') !== (booking.userId || '')
          ) {
            throw new Error('La prenotazione è stata modificata. Aggiorna la lista prima di cancellarla.');
          }
          if (currentBooking.status !== 'cancelled' && currentBooking.status !== 'canceled') {
            transaction.set(slotRef, {
              bookedCount: Math.max(0, getSlotBookedCount(slotSnap) - 1),
              updatedAt: serverTimestamp(),
            }, { merge: true });
          }

          if (lockRef && lockSnap?.exists() && (lockSnap.data() || {}).bookingId === booking.id) {
            transaction.delete(lockRef);
          }
          transaction.delete(bookingRef);
        });

        if (editingBookingId.value === booking.id) resetBookingEdit();
        bookingMessage.value = 'Prenotazione cancellata.';
        await refreshDashboard({ force: true });
      } catch (error) {
        console.error('Errore cancellazione prenotazione pubblica:', error);
        bookingError.value = error.message || 'Non riesco a cancellare la prenotazione.';
      } finally {
        bookingSaving.value = false;
      }
    };

    onMounted(() => {
      unsubscribeAuth = onAuthStateChanged(auth, (user) => {
        if (!user || currentAuthUser.value?.uid !== user.uid) {
          bookings.value = [];
          attendanceSaving.value = {};
          attendancePending.value = {};
          attendanceErrors.value = {};
          attendancePatches.clear();
          bookingSaving.value = false;
          timelineVisibleBookings.value = [];
          bookingView.value = 'current';
          serviceConfig.value = null;
          dashboardLoaded.value = false;
          historyRequested.value = false;
          historyLoaded.value = false;
          selectedDate.value = '';
          selectedMeetingMode.value = '';
          pastDatesOpen.value = false;
          scheduleEditorOpen.value = false;
          availableDatesOpen.value = false;
          inPersonBookingsEnabled.value = false;
        }
        currentAuthUser.value = user;
        // Wait for Firebase to restore the session before requesting protected data.
        refreshDashboard({ force: true });
        void openManualBookingFromDeepLink();
      });
    });

    onBeforeUnmount(() => {
      disposed = true;
      dashboardLoader.dispose();
      commercialStatusLoader.dispose();
      if (unsubscribeAuth) unsubscribeAuth();
    });

    return {
      currentBookingRows,
      blockDialogOpen, blockSaving, blockError, blockConflicts,
      openBookingBlock, closeBookingBlock, clearBookingBlockFeedback, createBookingBlock, removeBookingBlock,
      bookings,
      bookingView,
      openTimeline,
      timelineVisibleBookings,
      attendanceSaving,
      attendancePending,
      attendanceErrors,
      saveAttendance,
      saveBookingNote,
      commercialStatusByBookingId,
      purchaseInfo,
      situationInfo,
      selectedDate,
      selectedMeetingMode,
      toggleMeetingModeFilter,
      isLoading,
      dashboardLoaded,
      historyLoaded,
      inPersonBookingsEnabled,
      errorMessage,
      filtersOpen,
      pastDatesOpen,
      scheduleEditorOpen,
      availableDatesOpen,
      selectedConfigMonth,
      selectedConfigYear,
      monthOptions,
      configYearOptions,
      serviceLoading,
      serviceError,
      scheduleSaving,
      scheduleMessage,
      scheduleError,
      bookingSaving,
      bookingMessage,
      bookingError,
      editingDateKey,
      dateForm,
      editingBookingId,
      editingBooking,
      editingBookingLabel,
      bookingForm,
      manualBookingOpen,
      manualBookingMode,
      manualBookingForm,
      manualBookingAvailabilityLoading,
      manualBookingAvailabilityError,
      manualUserLookupLoading,
      manualUserCreateLoading,
      manualUserLookupMessage,
      manualUserLookupError,
      manualUserLookupCanCreate,
      reminderModalOpen,
      reminderForm,
      reminderSaving,
      reminderMessage,
      reminderError,
      reminderBookingLabel,
      canManagePublicCalendar,
      canViewPublicCalendar,
      availableDates,
      currentAndFutureAvailableDates,
      pastAvailableDates,
      configuredDates,
      monthFilteredConfiguredDates,
      filteredBookings,
      manualCalendarDates,
      manualCalendarTimeOptions,
      manualFreeCalendarSlots,
      manualFreeAvailableSlots,
      manualFreeCalendarSummary,
      manualFreeCalendarPanelVisible,
      manualFreeSlotStatusLabel,
      manualBookingScheduleInfo,
      manualBookingIsInternal,
      manualBookingScheduleBlocked,
      manualBookingSubmitLabel,
      todayDateKey,
      formVisibilityHint,
      bookingMissingPhone,
      openUserProfile,
      bookingIsOutsideCalendar,
      bookingOriginLabel,
      bookingOriginClass,
      bookingMeetingMode,
      bookingMeetingModeLabel,
      canEditBookingMeetingMode,
      commercialStatusIcon,
      commercialStatusClass,
      commercialStatusTitle,
      fetchBookings,
      fetchServiceConfig,
      ensureBookingHistory,
      formatDateLabel,
      resetDateForm,
      saveBookingDate,
      editBookingDate,
      toggleDateVisibility,
      deleteBookingDate,
      resetBookingEdit,
      openManualBooking,
      openManualBookingFromDeepLink,
      setManualBookingMode,
      handleManualBookingFreeDateChange,
      selectManualFreeSlot,
      resetManualBooking,
      clearManualUserLookup,
      findManualBookingUserByEmail,
      createManualUserFromButton,
      createManualBooking,
      openFollowUpReminder,
      resetFollowUpReminder,
      createFollowUpReminder,
      editBooking,
      saveBookingEdit,
      deleteBooking,
    };
  },
};
</script>

<style scoped>
.booking-view-action {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}
.booking-view-action > span { color: #64748b; font-size: 13px; }
.public-booking-attendance-cell { min-width: 135px; }
.public-bookings-list-table .public-booking-attendance-cell { text-align: left; }
.public-booking-attendance-cell :deep(.pba-title) { display: none; }

.content-container {
  padding-bottom: 64px;
}

.bookings-loading,
.bookings-load-error {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin: 16px 0;
  padding: 12px 16px;
  border: 1px solid #dbe4f0;
  border-radius: 12px;
  background: #f8fafc;
  color: #334155;
}

.bookings-loading .material-icons {
  color: #2563eb;
}

.bookings-load-error {
  border-color: #f5c4c4;
  background: #fff5f5;
  color: #991b1b;
}

.bookings-load-error > span {
  flex: 1 1 260px;
}

.dataset-summary {
  margin: 14px 0 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 0.9rem;
  color: #475569;
}

.dataset-summary span {
  display: inline-flex;
  align-items: center;
  min-height: 34px;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  background: #f8fafc;
  padding: 0 12px;
  font-weight: 700;
}

.card-surface {
  background: #ffffff;
  border-radius: 18px;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.08);
  padding: 18px 20px;
  margin-top: 18px;
}

.schedule-grid {
  display: grid;
  grid-template-columns: minmax(360px, 0.85fr) minmax(560px, 1.35fr);
  gap: 20px;
  align-items: start;
}

.schedule-grid--calendar-locked {
  grid-template-columns: minmax(0, 1fr);
}

.schedule-card {
  min-width: 0;
}

.schedule-editor-card {
  position: sticky;
  top: 18px;
  overflow: hidden;
  border: 1px solid #e8edf5;
  background:
    linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
}

.schedule-editor-card,
.schedule-editor-card * {
  box-sizing: border-box;
}

.schedule-editor-card::before {
  content: '';
  position: absolute;
  inset: 0 0 auto 0;
  height: 4px;
  background: linear-gradient(90deg, #2563eb, #dc2626);
}

.section-heading {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.section-heading h2 {
  margin: 0;
  color: #0f172a;
  font-size: 1.15rem;
}

.section-heading p {
  margin: 4px 0 0;
  color: #64748b;
  font-size: 0.9rem;
}

.section-kicker {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 8px;
  color: #2563eb;
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.section-kicker .material-icons {
  font-size: 18px;
}

.schedule-editor-heading {
  padding-bottom: 16px;
  border-bottom: 1px solid #e8edf5;
}

.schedule-form--editor {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
  align-items: stretch;
}

.form-block {
  min-width: 0;
  padding: 14px;
  border: 1px solid #e8edf5;
  border-radius: 16px;
  background: #f8fafc;
}

.form-block .toolbar-group {
  width: 100%;
  min-width: 0;
  flex: 1 1 auto;
}

.form-block-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  color: #0f172a;
  font-size: 0.9rem;
  font-weight: 800;
}

.form-block-title .material-icons {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 9px;
  background: #e0e7ff;
  color: #3730a3;
  font-size: 16px;
}

.time-fields {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  min-width: 0;
}

.visibility-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: end;
  min-width: 0;
}

.visibility-preview {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 122px;
  min-height: 42px;
  gap: 8px;
  border-radius: 12px;
  padding: 0 13px;
  font-size: 0.86rem;
  font-weight: 800;
  border: 1px solid transparent;
  white-space: nowrap;
}

.visibility-preview .material-icons {
  font-size: 19px;
}

.visibility-preview.is-public {
  background: #dcfce7;
  border-color: #bbf7d0;
  color: #166534;
}

.visibility-preview.is-hidden {
  background: #fee2e2;
  border-color: #fecaca;
  color: #991b1b;
}

.schedule-actions {
  align-self: end;
}

.schedule-actions--editor {
  justify-content: stretch;
}

.schedule-actions--editor .btn {
  justify-content: center;
  width: 100%;
}

.save-date-btn {
  min-height: 46px;
}

.save-date-btn .material-icons,
.compact-btn .material-icons {
  font-size: 18px;
}

.schedule-table-wrap {
  margin-top: 6px;
}

.schedule-table {
  min-width: 760px;
}

.month-filter-row {
  display: flex;
  align-items: flex-end;
  gap: 14px;
  flex-wrap: wrap;
  margin: 4px 0 14px;
  padding: 12px;
  border: 1px solid #e8edf5;
  border-radius: 14px;
  background: #f8fafc;
}

.toolbar-group--month {
  width: 170px;
}

.toolbar-group--year {
  width: 120px;
}

.month-filter-summary {
  display: inline-flex;
  align-items: center;
  min-height: 38px;
  border-radius: 999px;
  padding: 0 12px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 0.84rem;
  font-weight: 800;
}

.booking-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.58);
  backdrop-filter: blur(6px);
}

.booking-modal-card {
  box-sizing: border-box;
  width: min(560px, calc(100vw - 28px));
  max-height: calc(100vh - 48px);
  overflow: auto;
  border: 1px solid #e8edf5;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 30px 80px rgba(15, 23, 42, 0.28);
  padding: 20px;
  font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  color: #0f172a;
}

.booking-modal-card--wide {
  width: min(680px, calc(100vw - 28px));
}

.booking-modal-head {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
  padding-bottom: 14px;
  margin-bottom: 14px;
  border-bottom: 1px solid #e8edf5;
}

.modal-kicker {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 8px;
  color: #2563eb;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  line-height: 1.2;
  text-transform: uppercase;
}

.modal-kicker .material-icons {
  font-size: 16px;
}

.booking-modal-head h2 {
  margin: 0;
  color: #0f172a;
  font-size: 1.08rem;
  font-weight: 800;
  letter-spacing: 0;
  line-height: 1.2;
}

.booking-modal-subject {
  margin-top: 7px;
  max-width: 500px;
  color: #475569;
  font-size: 0.9rem;
  font-weight: 700;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.booking-modal-head p {
  margin: 6px 0 0;
  color: #64748b;
  font-size: 0.82rem;
  line-height: 1.45;
}

.modal-close-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
  color: #334155;
  cursor: pointer;
}

.modal-close-btn:hover:not(:disabled) {
  background: #eff6ff;
  color: #2563eb;
}

.modal-close-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.booking-edit-form {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 14px;
  row-gap: 12px;
  align-items: start;
}

.booking-edit-form .toolbar-group {
  min-width: 0;
}

.booking-edit-form .toolbar-label {
  font-size: 0.68rem;
  letter-spacing: 0.04em;
}

.booking-edit-form .input {
  box-sizing: border-box;
  height: 40px;
  min-height: 40px;
  padding: 8px 12px;
  border-radius: 10px;
  font-size: 0.86rem;
  font-weight: 600;
  line-height: 1.2;
  background: #ffffff;
}

.booking-edit-hint {
  color: #64748b;
  font-size: 0.72rem;
  line-height: 1.35;
}

.booking-edit-hint--danger {
  color: #b91c1c;
}

.manual-booking-mode-group,
.booking-meeting-mode-group {
  grid-column: 1 / -1;
}

.manual-booking-mode-switch {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 5px;
  padding: 5px;
  border: 1px solid #dbe3ef;
  border-radius: 13px;
  background: #f1f5f9;
}

.manual-booking-mode-option {
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
  min-height: 54px;
  border: 1px solid transparent;
  border-radius: 10px;
  padding: 8px 10px;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
}

.manual-booking-mode-option > .material-icons {
  flex: 0 0 auto;
  font-size: 20px;
}

.manual-booking-mode-option > span:last-child,
.manual-booking-mode-option strong,
.manual-booking-mode-option small {
  display: block;
}

.manual-booking-mode-option strong {
  color: #334155;
  font-size: 0.8rem;
  line-height: 1.2;
}

.manual-booking-mode-option small {
  margin-top: 3px;
  font-size: 0.68rem;
  font-weight: 600;
  line-height: 1.25;
}

.manual-booking-mode-option.active {
  border-color: #93c5fd;
  background: #ffffff;
  color: #2563eb;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.12);
}

.manual-booking-mode-option.active strong {
  color: #1d4ed8;
}

.manual-booking-mode-option:focus-visible {
  outline: 3px solid rgba(37, 99, 235, 0.3);
  outline-offset: 1px;
}

.manual-booking-mode-option:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.inline-mode-link {
  border: 0;
  padding: 0;
  background: transparent;
  color: #2563eb;
  cursor: pointer;
  font: inherit;
  font-weight: 800;
}

.inline-mode-link:hover {
  text-decoration: underline;
}

.booking-create-form {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.booking-create-form .toolbar-group {
  width: 100%;
}

.booking-create-form .input::placeholder {
  color: #64748b;
  font-weight: 500;
}

.manual-free-calendar-slots {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: 9px;
  min-width: 0;
  border: 1px solid #dbeafe;
  border-radius: 12px;
  padding: 11px 12px;
  background: #f8fbff;
}

.manual-free-calendar-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  color: #1e3a8a;
}

.manual-free-calendar-head > span:first-child,
.manual-free-calendar-head strong,
.manual-free-calendar-head small {
  display: block;
}

.manual-free-calendar-head strong {
  font-size: 0.8rem;
  line-height: 1.25;
}

.manual-free-calendar-head small {
  margin-top: 2px;
  color: #64748b;
  font-size: 0.7rem;
  font-weight: 600;
  line-height: 1.35;
}

.manual-free-calendar-summary {
  flex: 0 0 auto;
  border: 1px solid #bfdbfe;
  border-radius: 999px;
  padding: 4px 8px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 0.65rem;
  font-weight: 800;
  line-height: 1.2;
  white-space: nowrap;
}

.manual-free-slot-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(78px, 1fr));
  gap: 7px;
  max-height: 158px;
  overflow-y: auto;
  padding: 1px 2px 2px;
}

.manual-free-slot {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  min-width: 0;
  min-height: 43px;
  border: 1px solid transparent;
  border-radius: 9px;
  padding: 5px 7px;
  font-family: inherit;
  line-height: 1.15;
}

.manual-free-slot strong {
  font-size: 0.78rem;
}

.manual-free-slot small {
  font-size: 0.61rem;
  font-weight: 700;
}

.manual-free-slot.is-available {
  border-color: #bbf7d0;
  background: #f0fdf4;
  color: #166534;
  cursor: pointer;
}

.manual-free-slot.is-available:hover:not(:disabled) {
  border-color: #4ade80;
  background: #dcfce7;
}

.manual-free-slot.is-unavailable {
  border-color: #fcd34d;
  background: #fffbeb;
  color: #92400e;
  cursor: pointer;
}

.manual-free-slot.is-unavailable:hover:not(:disabled) {
  border-color: #f59e0b;
  background: #fef3c7;
}

.manual-free-slot.is-selected {
  border-color: #2563eb;
  background: #eff6ff;
  color: #1d4ed8;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.16);
}

.manual-free-slot:focus-visible {
  outline: 3px solid rgba(37, 99, 235, 0.28);
  outline-offset: 1px;
}

.manual-free-slot:disabled {
  cursor: not-allowed;
  opacity: 1;
}

.manual-free-calendar-feedback,
.manual-free-time-help {
  color: #64748b;
  font-size: 0.7rem;
  font-weight: 600;
  line-height: 1.4;
}

.manual-free-calendar-feedback.is-error {
  color: #b91c1c;
}

.manual-free-time-help {
  color: #334155;
}

.manual-booking-schedule-info {
  grid-column: 1 / -1;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  border: 1px solid transparent;
  border-radius: 12px;
  padding: 11px 12px;
}

.manual-booking-schedule-info > .material-icons {
  flex: 0 0 auto;
  margin-top: 1px;
  font-size: 20px;
}

.manual-booking-schedule-info strong,
.manual-booking-schedule-info small {
  display: block;
}

.manual-booking-schedule-info strong {
  font-size: 0.8rem;
  line-height: 1.25;
}

.manual-booking-schedule-info small {
  margin-top: 3px;
  font-size: 0.74rem;
  font-weight: 600;
  line-height: 1.4;
}

.manual-booking-schedule-info.is-public {
  border-color: #bbf7d0;
  background: #f0fdf4;
  color: #166534;
}

.manual-booking-schedule-info.is-scheduled {
  border-color: #bfdbfe;
  background: #eff6ff;
  color: #1e40af;
}

.manual-booking-schedule-info.is-internal,
.manual-booking-schedule-info.is-occupied {
  border-color: #fde68a;
  background: #fffbeb;
  color: #92400e;
}

.manual-booking-schedule-info.is-closed,
.manual-booking-schedule-info.is-blocked {
  border-color: #fecaca;
  background: #fef2f2;
  color: #991b1b;
}

.btn.primary.manual-booking-submit.is-internal {
  background: linear-gradient(135deg, #d97706, #ea580c);
}

.follow-up-reminder-form {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.follow-up-note-group {
  grid-column: 1 / -1;
}

.booking-note-group {
  grid-column: 1 / -1;
}

.booking-edit-form .follow-up-note {
  height: auto;
  min-height: 120px;
  resize: vertical;
  line-height: 1.45;
  font-weight: 600;
}

.booking-edit-form .booking-note-input {
  height: auto;
  min-height: 96px;
  resize: vertical;
  line-height: 1.45;
  font-weight: 600;
}

.email-lookup-row {
  display: flex;
  gap: 8px;
  align-items: stretch;
  min-width: 0;
  width: 100%;
}

.email-lookup-row .input {
  flex: 1 1 auto;
  width: auto;
}

.email-search-btn {
  box-sizing: border-box;
  flex: 0 0 74px;
  min-width: 74px;
  height: 40px;
  justify-content: center;
  padding: 0 10px;
  border-radius: 10px;
  font-size: 0.8rem;
  line-height: 1;
}

.lookup-feedback {
  display: block;
  margin-top: 2px;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1.3;
}

.lookup-feedback.success {
  color: #047857;
}

.lookup-feedback.danger {
  color: #b91c1c;
}

.lookup-actions {
  margin-top: 6px;
}

.create-user-btn {
  min-height: 34px;
  padding: 7px 10px;
  border-radius: 10px;
  font-size: 0.78rem;
}

.create-user-btn .material-icons {
  font-size: 17px;
}

.booking-modal-actions {
  grid-column: 1 / -1;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 4px;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.18s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.date-public-state {
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  border-radius: 999px;
  padding: 0 10px;
  font-size: 0.76rem;
  font-weight: 800;
}

.date-public-state.ok {
  background: #dcfce7;
  color: #166534;
}

.date-public-state.muted-state {
  background: #e2e8f0;
  color: #475569;
}

.toolbar-card {
  margin-top: 24px;
}

.filters-header {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.filters-header--with-action {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: stretch;
}

.filters-header--with-action .accordion-toggle {
  min-width: 0;
  width: 100%;
  text-align: left;
}

.filters-header--with-action .toggle-copy {
  min-width: 0;
}

.accordion-toggle {
  flex: 1 1 320px;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #0f172a;
  cursor: pointer;
  transition: border 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}

.accordion-toggle:hover {
  background: #fff;
  border-color: #cbd5e1;
  box-shadow: 0 12px 26px rgba(15, 23, 42, 0.08);
}

.accordion-toggle .material-icons {
  font-size: 22px;
  color: #475569;
}

.past-dates-disclosure {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
}

.past-dates-heading {
  margin: 0;
}

.accordion-toggle--nested {
  width: 100%;
  flex: none;
  padding: 10px 12px;
  box-shadow: none;
  text-align: left;
}

.accordion-panel--nested {
  margin-top: 8px;
  padding: 10px 12px 12px;
  border-color: #e2e8f0;
  background: #fff;
  box-shadow: none;
}

.accordion-panel--nested .date-chips {
  margin-top: 0;
}

.toggle-copy {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.toggle-title {
  font-weight: 700;
  font-size: 0.95rem;
}

.toggle-subtitle {
  font-size: 0.82rem;
  color: #64748b;
}

.accordion-panel {
  margin-top: 14px;
  padding: 14px 12px;
  border-radius: 14px;
  border: 1px solid #f1f5f9;
  background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.6);
}

.accordion-panel--plain {
  padding: 16px 0 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
}

.accordion-enter-active,
.accordion-leave-active {
  transition: all 0.18s ease;
}

.accordion-enter,
.accordion-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
}

.toolbar-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 0 0 auto;
}

.toolbar-group--sm {
  width: 160px;
}

.toolbar-group--xs {
  width: 112px;
}

.toolbar-actions {
  display: flex;
  gap: 12px;
  flex: 0 0 auto;
  align-items: center;
  flex-wrap: wrap;
}

.toolbar-label {
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
}

.date-range-input {
  width: 100%;
}

.date-range-input--short {
  width: 82% !important;
}

.input {
  display: block;
  min-width: 0;
  max-width: 100%;
  width: 100%;
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #0f172a;
  transition: border 0.2s ease, box-shadow 0.2s ease;
}

.input:focus {
  border-color: #2563eb;
  background: #fff;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
  outline: none;
}

.input--large {
  min-height: 44px;
  font-weight: 700;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border-radius: 12px;
  border: 1px solid transparent;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
  background: #f1f5f9;
  color: #1e293b;
  border-color: #e2e8f0;
}

.btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.12);
}

.btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  box-shadow: none;
}

.btn.primary {
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  color: #fff;
}

.btn.ghost {
  background: #f1f5f9;
  color: #1e293b;
  border-color: #e2e8f0;
}

.booking-add-action {
  justify-content: center;
  min-height: 40px;
  padding: 8px 12px;
  font-size: 0.82rem;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
}

.booking-add-action .material-icons {
  font-size: 18px;
  color: inherit;
}

.date-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
}

.date-chips button {
  display: inline-flex;
  align-items: center;
  min-height: 34px;
  padding: 0 12px;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  background: #f1f5f9;
  color: #1e293b;
  cursor: pointer;
  font-weight: 700;
  font-size: 0.82rem;
}

.date-chips button.active {
  border-color: #bfdbfe;
  background: #eff6ff;
  color: #2563eb;
}

.meeting-mode-filters {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin: 20px 0 18px;
  padding: 16px;
  border: 1px solid #cbd8f4;
  border-radius: 18px;
  background: linear-gradient(115deg, #edf4ff, #f4f1ff);
  box-shadow: 0 4px 16px rgba(49, 74, 135, 0.06);
}

.meeting-mode-filters__label {
  grid-column: 1 / -1;
  color: #405681;
  font-size: 0.74rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.meeting-mode-filters button {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  min-width: 0;
  min-height: 64px;
  padding: 10px 14px;
  border: 1px solid #d6def0;
  border-radius: 13px;
  background: #fff;
  color: #233656;
  box-shadow: 0 2px 5px rgba(30, 50, 100, 0.04);
  font-size: 0.95rem;
  font-weight: 800;
  line-height: 1.25;
  text-align: left;
  cursor: pointer;
  transition: border-color 160ms ease, box-shadow 160ms ease;
}

.meeting-mode-filters button:hover {
  opacity: 1;
  border-color: #91aff0;
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.12);
}

.meeting-mode-filters button.active {
  border-color: transparent;
  background: linear-gradient(120deg, #2563eb, #4f46e5);
  color: #fff;
  box-shadow: 0 6px 16px rgba(55, 78, 212, 0.24);
}

.meeting-mode-filters .material-icons {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #edf2ff;
  color: #365cbd;
  font-size: 24px;
}

.meeting-mode-filters button.active .material-icons {
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
}

@media (max-width: 480px) {
  .meeting-mode-filters {
    gap: 10px;
    padding: 12px;
  }

  .meeting-mode-filters button {
    flex-direction: column;
    justify-content: center;
    gap: 8px;
    padding: 12px 6px;
    font-size: 0.85rem;
    text-align: center;
  }
}

.accordion-toggle:focus-visible,
.booking-add-action:focus-visible,
.meeting-mode-filters button:focus-visible,
.date-chips button:focus-visible {
  outline: 3px solid rgba(37, 99, 235, 0.35);
  outline-offset: 2px;
}

.feedback-row {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  font-size: 0.85rem;
}

.schedule-feedback {
  margin-top: 14px;
}

.feedback-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  border-radius: 12px;
  padding: 10px 12px;
  font-weight: 700;
  line-height: 1.35;
}

.feedback-pill .material-icons {
  flex: 0 0 auto;
  font-size: 18px;
}

.feedback-pill.success {
  background: #dcfce7;
  color: #166534;
}

.feedback-pill.info {
  background: #eff6ff;
  color: #1d4ed8;
}

.feedback-pill.danger {
  background: #fee2e2;
  color: #991b1b;
}

.tickets {
  overflow-x: auto;
}

.users-table {
  width: 100%;
  min-width: 1060px;
  border-collapse: collapse;
  background: #fff;
}

.users-table th,
.users-table td {
  padding: 12px 10px;
  border-bottom: 1px solid #e2e8f0;
  text-align: left;
  vertical-align: middle;
}

.users-table th {
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
}

.users-table td {
  color: #1e293b;
  font-size: 0.92rem;
}

.users-table tr.booking-row--missing-phone > td {
  background: #fff7ed;
}

.users-table tr.booking-row--missing-phone > td:first-child {
  box-shadow: inset 4px 0 0 #f97316;
}

.users-table tr.booking-row--in-person > td {
  background: #eff6ff;
}

.users-table tr.booking-row--in-person.booking-row--missing-phone > td:nth-child(4) {
  background: #fff7ed;
}

.booking-date-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.booking-meeting-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 20px;
  color: #475569;
}

.booking-meeting-icon .material-icons {
  font-size: 20px;
}

.booking-meeting-icon.is-in-person {
  color: #1e40af;
}

.users-table td strong,
.users-table td small {
  display: block;
}

.users-table td small {
  margin-top: 4px;
  color: #64748b;
  font-size: 0.72rem;
}

.booking-note-cell {
  max-width: 240px;
  color: #475569;
  font-size: 0.84rem;
  line-height: 1.35;
  white-space: pre-wrap;
}

.booking-note-cell > .booking-inline-note {
  width: 100%;
}

@media (min-width: 769px) {
  .booking-note-cell {
    min-width: 220px;
  }
}

.booking-origin-badge {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  margin-top: 6px;
  border-radius: 999px;
  padding: 0 9px;
  font-size: 0.7rem;
  font-weight: 800;
  text-transform: uppercase;
}

.booking-origin-badge.is-site {
  background: #dbeafe;
  color: #1d4ed8;
}

.booking-origin-badge.is-manual {
  background: #fef3c7;
  color: #92400e;
}

.booking-origin-badge.is-outside-calendar {
  background: #ffedd5;
  color: #9a3412;
}

.booking-origin-badge.is-unknown {
  background: #e2e8f0;
  color: #475569;
}

.commercial-status-cell {
  text-align: center;
}

.commercial-status-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #475569;
}

.commercial-status-icon .material-icons {
  font-size: 19px;
}

.commercial-status-icon.is-active-program {
  border-color: #bfdbfe;
  background: #dbeafe;
  color: #1d4ed8;
}

.commercial-status-icon.is-purchased {
  border-color: #bbf7d0;
  background: #dcfce7;
  color: #166534;
}

.commercial-status-icon.is-lead {
  border-color: #fed7aa;
  background: #fff7ed;
  color: #c2410c;
}

.commercial-status-icon.is-loading {
  border-color: #fde68a;
  background: #fef3c7;
  color: #92400e;
}

.commercial-status-icon.is-error {
  border-color: #fecaca;
  background: #fee2e2;
  color: #991b1b;
}

.commercial-status-icon.is-unknown {
  border-color: #e2e8f0;
  background: #f1f5f9;
  color: #64748b;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  border-radius: 999px;
  background: #e2e8f0;
  color: #334155;
  font-size: 0.76rem;
  font-weight: 800;
  padding: 0 10px;
  text-transform: uppercase;
}

.status-badge.booked,
.status-badge.confirmed {
  background: #dcfce7;
  color: #166534;
}

.status-badge.pending {
  background: #fef3c7;
  color: #92400e;
}

.status-badge.cancelled,
.status-badge.canceled {
  background: #fee2e2;
  color: #991b1b;
}

.buttons-container {
  display: flex;
  gap: 8px;
}

.buttons-container a,
.buttons-container button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid #dbeafe;
  background: #eff6ff;
  color: #2563eb;
  cursor: pointer;
  text-decoration: none;
}

.buttons-container a:hover,
.buttons-container button:hover:not(:disabled) {
  background: #dbeafe;
}

.buttons-container button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.mono {
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
}

.muted {
  color: #64748b;
}

.error {
  color: #b91c1c;
}

.empty-state {
  margin-top: 16px;
}

@media (max-width: 1180px) {
  .schedule-grid {
    grid-template-columns: 1fr;
  }

  .schedule-editor-card {
    position: static;
  }
}

@media (max-width: 760px) {
  .card-surface {
    padding: 16px;
  }

  .schedule-grid,
  .schedule-form--editor,
  .booking-edit-form,
  .time-fields,
  .visibility-row {
    grid-template-columns: 1fr;
  }

  .booking-modal-backdrop {
    align-items: flex-end;
    padding: 14px;
  }

  .booking-modal-card {
    max-height: calc(100vh - 28px);
    border-radius: 18px;
    padding: 18px;
  }

  .booking-modal-head {
    gap: 12px;
  }

  .booking-modal-actions {
    justify-content: stretch;
  }

  .booking-modal-actions .btn {
    width: 100%;
    justify-content: center;
  }

  .visibility-preview {
    width: 100%;
  }

  .toolbar-group--xs,
  .toolbar-group--sm,
  .toolbar-group--month,
  .toolbar-group--year {
    width: 100%;
  }

  .date-range-input--short {
    width: 100% !important;
  }
}
</style>

<style scoped src="../../assets/css/public-bookings-mobile.css"></style>
<style scoped src="../../assets/css/public-bookings-mobile-forms.css"></style>
<style scoped>
.booking-create-actions { display: flex; align-items: stretch; gap: 10px; }
.booking-create-actions .btn { white-space: nowrap; }
.booking-block-action { color: #785013; background: #fff8eb; border-color: #e4c892; }
.public-bookings-list-table .public-booking-block-row { background: #fff8eb; }
.public-bookings-list-table .public-booking-block-row > td { padding-top: 18px; padding-bottom: 18px; vertical-align: middle; background: #fff8eb; border-bottom-color: #ecd8b1; color: #785013; }
.public-bookings-list-table .public-booking-block-row > td:first-child { border-left: 4px solid #d99a33; }
.public-booking-block-detail { text-align: left; }
.public-booking-block-interval { display: inline-flex; align-items: center; gap: 6px; white-space: nowrap; font-variant-numeric: tabular-nums; font-size: 13px; }
.public-booking-block-summary { display: grid; grid-template-columns: 36px minmax(0, 1fr); align-items: center; gap: 12px; }
.public-booking-block-summary > .public-booking-block-icon { display: grid; place-items: center; width: 36px; height: 36px; margin: 0; border-radius: 10px; background: #f9e7c4; color: #946015; font-size: 20px; line-height: 1; }
.public-booking-block-copy { display: grid; gap: 4px; min-width: 0; }
.public-bookings-list-table .public-booking-block-title { margin: 0; color: #785013; font-size: 14px; line-height: 1.4; }
.public-booking-block-note { display: block; margin: 0; font-size: 13px; line-height: 1.45; white-space: pre-wrap; overflow-wrap: anywhere; }
.public-bookings-list-table .public-booking-block-detail small { display: block; margin: 0; color: #85673d; font-size: 12px; line-height: 1.45; }
.public-booking-block-actions .btn { white-space: nowrap; }
.manual-booking-schedule-info.is-blocked { border-color: #e7bd79; background: #fff7e8; color: #79440d; }
@media (max-width: 1100px) {
  .public-bookings-page .filters-header--with-action { grid-template-columns: minmax(0, 1fr); }
  .booking-create-actions { justify-content: flex-end; }
}
@media (max-width: 768px) {
  .booking-create-actions { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
  .public-bookings-page .toolbar-card .booking-create-actions .btn { width: 100%; padding: 10px 8px; min-height: 48px; font-size: 13px; white-space: normal; }
  .public-bookings-page .public-bookings-mobile-table tbody tr.public-booking-block-row { background: #fff8eb; border-color: #ecd8b1; border-left: 4px solid #d99a33; }
  .public-bookings-page .public-bookings-mobile-table .public-booking-block-row > td { padding: 0; border: 0; background: transparent; color: #785013; }
  .public-bookings-page .public-bookings-mobile-table .public-booking-block-detail,
  .public-bookings-page .public-bookings-mobile-table .public-booking-block-actions { grid-column: 1 / -1; }
  .public-bookings-page .public-bookings-mobile-table .public-booking-block-time { align-items: flex-end; text-align: right; }
  .public-bookings-page .public-bookings-mobile-table .public-booking-block-detail { padding-top: 14px; border-top: 1px solid #ecd8b1; }
  .public-booking-block-summary { width: 100%; grid-template-columns: 32px minmax(0, 1fr); gap: 10px; }
  .public-booking-block-summary > .public-booking-block-icon { width: 32px; height: 32px; }
  .public-bookings-page .public-booking-block-detail::before,
  .public-bookings-page .public-booking-block-actions::before { display: none; }
  .public-booking-block-actions .btn { width: 100%; justify-content: center; min-height: 44px; }
}
</style>
